import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    IconButton,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stepper,
    Step,
    StepLabel,
    InputAdornment,
    CircularProgress,
    Radio,
    FormControlLabel,
    RadioGroup,
    useMediaQuery,
    useTheme,
    Checkbox,
    Chip,
    Stack,
    Divider,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
    Alert,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { notifyPermissionDenied } from '../../utils/apiPermissionError';
import { useAuth } from '../../contexts/AuthContext';
import { useSucursalesUsuarioContext } from '../../contexts/SucursalesUsuarioContext';
import { useSucursalAltaFormulario } from '../../hooks/useSucursalAltaFormulario';
import api from '../../services/api';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { PersonSearch, Edit, DeleteOutline } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description';
import NotesIcon from '@mui/icons-material/Notes';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ProveedorSearch from './ProveedorSearch';
import ProveedorInlineSearchField from '../ProveedorInlineSearchField';
import ObservacionesConNotaProveedor from '../ObservacionesConNotaProveedor';
import { textoNotasPublicasProveedor } from '../../utils/proveedorNotas';
import AplicarSaldoReciboDialog from './AplicarSaldoReciboDialog';
import proveedorService from '../../services/proveedorService';
import { formatCurrency, roundMoney, truncToDecimals } from '../../utils/formatters';
import {
    calcularImporteSugeridoPago,
    calcularTotalMedios,
    calcularTotalesTesoreria,
    montoImputableComprobante,
    normalizeImporteMedio,
} from '../../utils/tesoreriaTotales';
import tesoreriaService, { GrupoCajaChica, MedioTransferencia, MarcaTarjeta, Banco, ChequeCartera } from '../../services/tesoreriaService';
import tesoreriaAplicacionesService from '../../services/tesoreriaAplicacionesService';
import cuentaCorrienteProveedoresService from '../../services/cuentaCorrienteProveedoresService';
import carteraService from '../../services/carteraService';
import companiaService, { Compania } from '../../services/companiaService';
import SearchableSelect from '../SearchableSelect';
import CurrencyTextField from '../shared/CurrencyTextField';
import GrupoCajaSelect from './GrupoCajaSelect';
import { findGrupoCajaGuardado, persistGrupoCajaChica } from './grupoCajaStorage';
import { esDebitoPagoProveedor, esNotaCreditoProveedor } from '../../constants/tiposComprobante';
import { esChequeDisponibleEnCartera } from '../../utils/chequeTerceroEstado';

const TIPO_PAGO_OPTIONS = [
    { value: 'Efectivo', label: 'Efectivo' },
    { value: 'Transferencia', label: 'Transferencias' },
    { value: 'Cheque', label: 'Cheques de Tercero' },
    { value: 'ChequePropio', label: 'Cheque Propio' },
    { value: 'Tarjeta', label: 'Tarjetas' },
    { value: 'Otros', label: 'Otros' },
] as const;

const labelTipoPagoOp = (tipoPago?: string): string => {
    const found = TIPO_PAGO_OPTIONS.find((o) => o.value === tipoPago);
    return found?.label ?? (tipoPago || '').toUpperCase();
};

/** Librador + nroLibrador (CUIT) desde la compañía emisora. */
function libradorDesdeCompania(compania: Compania): { librador: string; nroLibrador: string } {
    const librador = String(compania.nombre || '').trim().slice(0, 25);
    const nroLibrador = String(compania.cuit || '').replace(/\D/g, '').slice(0, 20);
    return { librador, nroLibrador };
}

type BancoDesdeCarteraResult = {
    banco: string;
    cartera: number;
    nroBanco?: number;
    nroSucursal?: number;
    ctaBancaria?: string;
    cbu?: string;
    /** Advertencias por datos incompletos en la ficha de cartera (solo CBU). */
    advertencias: string[];
};

const enRangoChequeBanco = (n: number) => Number.isFinite(n) && n >= 1 && n <= 999;

/** Solo CBU: con él se derivan N° Banco y N° Sucursal del cheque. */
function advertenciasDesdeCartera(cartera: { cbu?: string }): string[] {
    const cbu = String(cartera.cbu || '').replace(/\D/g, '');
    if (!cbu) {
        return ['Falta CBU en la cartera. Completalo en la ficha para poder emitir el cheque propio.'];
    }
    if (cbu.length < 22) {
        return [`El CBU de la cartera está incompleto (${cbu.length} dígitos; se esperan 22).`];
    }
    return [];
}

/** Preficha cartera + códigos de cheque (NroBanco/Sucursal 1–999) desde el MCP de cheque propio. */
async function bancoDesdeCarteraMcp(
    medio: MedioTransferencia
): Promise<BancoDesdeCarteraResult> {
    const nroCartera = Number(medio.cartera);
    if (!Number.isFinite(nroCartera) || nroCartera <= 0) {
        return {
            banco: (medio.nombreBanco || medio.nombre || '').trim() || String(medio.cartera ?? ''),
            cartera: 0,
            advertencias: ['El medio de cheque propio no tiene cartera asociada.'],
        };
    }

    try {
        const cartera = await carteraService.getCartera(nroCartera);
        const nombre = (cartera.nombreBanco || '').trim() || medio.nombreBanco || medio.nombre || '';
        const label = `${nombre}${cartera.nroBanco ? ` (${cartera.nroBanco})` : ''}`;

        let nroBanco: number | undefined;
        let nroSucursal: number | undefined;

        // CBU: posiciones 1–3 banco, 4–7 sucursal (códigos BCRA 1–999)
        const cbu = String(cartera.cbu || '').replace(/\D/g, '');
        if (cbu.length >= 7) {
            const fromCbuBanco = parseInt(cbu.slice(0, 3), 10);
            const fromCbuSuc = parseInt(cbu.slice(3, 7), 10);
            if (enRangoChequeBanco(fromCbuBanco)) nroBanco = fromCbuBanco;
            if (enRangoChequeBanco(fromCbuSuc)) nroSucursal = fromCbuSuc;
        }

        // Sucursal numérica en la ficha de cartera
        if (nroSucursal == null) {
            const sucParsed = parseInt(String(cartera.sucursal || '').replace(/\D/g, ''), 10);
            if (enRangoChequeBanco(sucParsed)) nroSucursal = sucParsed;
        }

        // Maestro de bancos (1–999) por nombre de la cartera
        if (nroBanco == null && nombre) {
            const term = nombre.replace(/^(bco\.?|banco)\s+/i, '').trim();
            if (term) {
                try {
                    const bancos = await tesoreriaService.getBancosPaged({
                        searchTerm: term,
                        pageNumber: 1,
                        pageSize: 20,
                    });
                    const match = bancos.find((b) => enRangoChequeBanco(Number(b.nroBanco)));
                    if (match) nroBanco = Number(match.nroBanco);
                } catch {
                    /* ignore */
                }
            }
        }

        // Liber Banco no pide sucursal: si hay banco válido y no pudimos derivar sucursal, usar 1
        if (nroBanco != null && nroSucursal == null) {
            nroSucursal = 1;
        }

        const ctaBancaria = (cartera.nroDeCuenta || '').trim() || undefined;
        const advertencias = advertenciasDesdeCartera(cartera);

        return {
            banco: label,
            cartera: nroCartera,
            nroBanco,
            nroSucursal,
            ctaBancaria,
            cbu: cbu || undefined,
            advertencias,
        };
    } catch {
        const nombre = (medio.nombreBanco || medio.nombre || '').trim();
        return {
            banco: nombre ? `${nombre} (${nroCartera})` : String(nroCartera),
            cartera: nroCartera,
            advertencias: [
                `No se pudo cargar la cartera N° ${nroCartera}. Verificá que exista y tenga CBU cargado.`,
            ],
        };
    }
}

interface FormularioData {
    nroFormulario?: number;
    tipoProveedor: string;
    nroTipoFormulario: string;
    nroSuc: number;
    fechaEmision: string;
    nroProveedor: number;
    nombre: string;
    tipoIVA: string;
    cuit: string;
    importeTotal: number;
    descripcion: string;
    usuario: string;
    letra?: string;
    nroComprobante?: number;
    subtotal: number;
    iva: number;
    impuestoInterno?: number;
}

interface TipoPagoData {
    nroTipoPago?: number;
    nroFormulario?: number;
    tipoPago: string;
    ingresoEgreso: string;
    detalle: string;
    // Etiqueta de UI para mostrar el medio elegido (no se envía al backend)
    medioLabel?: string;
    importe: number;
    nroCuenta?: number;
    fecha?: string;
    grupoMcp?: number;
    // Identificador del medio de cobro (para el endpoint)
    mcp?: string;
    /** Nro. de cartera bancaria del MCP (p. ej. 1003). Distinto del nroBanco del cheque (1–999). */
    cartera?: number;
    // Campos para Cheques de Tercero / Propio
    banco?: string;
    nroBanco?: number;
    nroSucursal?: number;
    nroCheque?: string;
    fechaEmision?: string;
    fechaVencimiento?: string;
    ctaBancaria?: string;
    librador?: string;
    nroLibrador?: string;
    concepto?: string;
    nroCarga?: number; // Número de carga del cheque de cartera
    // Campos para Tarjetas
    nroTarjeta?: string;
    nroComprobanteTa?: number;
    numeroroAutorizacion?: string;
    codigoMarca?: number;
    telefono?: string;
    fechaPresentacion?: string;
    titular?: string;
}

interface ComprobanteDeuda {
    id: number;
    nroComprobante: string;
    tipoComprobante: string;
    nroTipoFormulario?: string;
    fechaEmision: string;
    fechaVencimiento: string;
    importe: number;
    saldo: number;
    vencido: boolean;
    nroFormulario?: number;
}

const textoComprobanteDeuda = (comp: ComprobanteDeuda): string =>
    `${comp.tipoComprobante} ${comp.nroComprobante}`.trim();

const esComprobanteImputableOrdenPago = (comp: ComprobanteDeuda): boolean =>
    esDebitoPagoProveedor(comp.nroTipoFormulario ?? '', comp.tipoComprobante);

const esNcVisibleEnOrdenPago = (
    comp: ComprobanteDeuda,
    ncConSaldoPendiente?: Set<number> | null
): boolean => {
    if (!esNotaCreditoProveedor(comp.nroTipoFormulario ?? '', textoComprobanteDeuda(comp))) {
        return false;
    }
    const nro = Number(comp.nroFormulario);
    if (ncConSaldoPendiente != null) {
        return Number.isFinite(nro) && nro > 0 && ncConSaldoPendiente.has(nro);
    }
    return Math.abs(Number(comp.saldo ?? 0)) > 0.00001;
};

const esComprobanteVisibleOrdenPago = (
    comp: ComprobanteDeuda,
    ncConSaldoPendiente?: Set<number> | null
): boolean =>
    esComprobanteImputableOrdenPago(comp) || esNcVisibleEnOrdenPago(comp, ncConSaldoPendiente);

const TIPO_IVA_LABELS: Record<string, string> = {
    I: 'Inscripto',
    N: 'No Inscripto',
    E: 'Exento',
    M: 'Monotributo',
    RI: 'Resp. Inscripto',
    CF: 'Consumidor Final',
    EX: 'Exento',
    MO: 'Monotributista',
    NI: 'No Inscripto',
};

// Se cargan dinámicamente desde API

const initialPago: TipoPagoData = {
    tipoPago: '',
    ingresoEgreso: 'I',
    detalle: '',
    importe: 0,
    fecha: new Date().toISOString().split('T')[0],
    fechaEmision: new Date().toISOString().split('T')[0],
    fechaVencimiento: new Date().toISOString().split('T')[0]
};

const limpiarTextoCheque = (texto: string | undefined | null, maxLength: number): string => {
    if (!texto) return '';
    return String(texto).trim().slice(0, maxLength);
};

const toFechaIsoDate = (value: string | null | undefined): string => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0];
};

/** Lee un campo del cheque tolerando PascalCase del backend / state de navegación. */
const campoCheque = (cheque: ChequeCartera | Record<string, unknown>, ...keys: string[]): unknown => {
    const raw = cheque as Record<string, unknown>;
    for (const key of keys) {
        if (raw[key] != null && String(raw[key]).trim() !== '') return raw[key];
    }
    return undefined;
};

/** Convierte un cheque de cartera en renglón de medio de pago (endoso en OP). */
const chequeCarteraADetallePago = (cheque: ChequeCartera): TipoPagoData => {
    const nroCheque = String(campoCheque(cheque, 'nroCheque', 'NroCheque') ?? '');
    const banco = String(campoCheque(cheque, 'nombreBanco', 'NombreBanco') ?? '').trim();
    const libradorRaw = String(
        campoCheque(cheque, 'librador', 'Librador', 'nroLibrador', 'NroLibrador') ?? ''
    ).trim();
    const nroLibradorRaw = String(campoCheque(cheque, 'nroLibrador', 'NroLibrador') ?? '').trim();
    return {
        tipoPago: 'Cheque',
        ingresoEgreso: 'I',
        detalle: '',
        medioLabel: `Cheque ${nroCheque}${banco ? ` · ${banco}` : ''}`,
        importe: normalizeImporteMedio(Number(campoCheque(cheque, 'importe', 'Importe') ?? 0)),
        fecha: new Date().toISOString().split('T')[0],
        mcp: String(campoCheque(cheque, 'mcp', 'Mcp', 'MCP') ?? '').trim(),
        nroCarga: Number(campoCheque(cheque, 'nroCarga', 'NroCarga') ?? 0) || undefined,
        nroBanco: Number(campoCheque(cheque, 'nroBanco', 'NroBanco') ?? 0),
        banco,
        nroSucursal: Number(campoCheque(cheque, 'nroSucursal', 'NroSucursal') ?? 0),
        nroCheque,
        fechaEmision: toFechaIsoDate(
            String(campoCheque(cheque, 'fechaEmision', 'FechaEmision') ?? '') || undefined
        ),
        fechaVencimiento: toFechaIsoDate(
            String(campoCheque(cheque, 'fechaVencimiento', 'FechaVencimiento') ?? '') || undefined
        ),
        ctaBancaria: limpiarTextoCheque(
            String(campoCheque(cheque, 'nroCuenta', 'NroCuenta') ?? ''),
            20
        ),
        librador: limpiarTextoCheque(libradorRaw || nroLibradorRaw, 25),
        nroLibrador: limpiarTextoCheque(nroLibradorRaw, 20),
        concepto: limpiarTextoCheque(
            String(campoCheque(cheque, 'concepto', 'Concepto') ?? ''),
            255
        ),
    };
};

/** Efectivo: permite vuelto (importe negativo). Otros medios: solo importe positivo. */
function importePagoDialogValidoOp(importe: number, tipoPagoSeleccionado: string): boolean {
    const imp = Number(importe);
    if (!Number.isFinite(imp) || imp === 0) return false;
    if ((tipoPagoSeleccionado || '').toLowerCase() === 'efectivo') return true;
    return imp > 0;
}

/** Línea de medio lista para enviar (vuelto solo en efectivo). */
function importeMedioOrdenPagoValidoParaEnvio(pago: { tipoPago?: string; importe?: number }): boolean {
    if (!pago.tipoPago) return false;
    const imp = Number(pago.importe);
    if (!Number.isFinite(imp) || imp === 0) return false;
    if ((pago.tipoPago || '').toLowerCase() === 'efectivo') return true;
    return imp > 0;
}

const NuevaOrdenPago: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const _isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTabletOrLarger = useMediaQuery(theme.breakpoints.up('md'));
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();
    const loggedUsername = user?.username || localStorage.getItem('userName') || localStorage.getItem('username') || localStorage.getItem('user') || 'Sistema';

    const [formulario, setFormulario] = useState<FormularioData>({
        tipoProveedor: 'C',
        nroTipoFormulario: '4',
        nroSuc: 0,
        fechaEmision: new Date().toISOString().split('T')[0],
        nroProveedor: 0,
        nombre: '',
        tipoIVA: '',
        cuit: '',
        importeTotal: 0,
        descripcion: '',
        usuario: loggedUsername,
        subtotal: 0,
        iva: 0
    });

    const [detallesPago, setDetallesPago] = useState<TipoPagoData[]>([]);

    const [openPagoDialog, setOpenPagoDialog] = useState(false);
    const [pagoActual, setPagoActual] = useState<TipoPagoData>(initialPago);
    /** Cheques traídos desde cartera (endoso); se precargan como medios y se premarcan en el diálogo. */
    const [nrosCargaDesdeCartera, setNrosCargaDesdeCartera] = useState<number[]>([]);
    const preseleccionChequesOpRef = React.useRef(false);
    const chequesPrecargadosDesdeCarteraRef = React.useRef(false);

    /** Abre el detalle del comprobante de deuda (factura/ND de compra), no de la orden de pago. */
    const handleVerDetalles = (comp: ComprobanteDeuda) => {
        const id = Number(comp.nroFormulario);
        if (!Number.isFinite(id) || id <= 0) {
            enqueueSnackbar('Este comprobante no tiene nroFormulario para consultar detalle.', { variant: 'info' });
            return;
        }

        const nroTipo = (comp.nroTipoFormulario ?? '').toString().trim();
        const textoDocumento = `${comp.tipoComprobante} ${comp.nroComprobante}`.trim();
        let path: string | null = null;

        if (esDebitoPagoProveedor(nroTipo, textoDocumento)) {
            path = `/compras/facturas/${id}`;
        } else if (esNotaCreditoProveedor(nroTipo, textoDocumento)) {
            path = `/compras/notas-credito/${id}`;
        }

        if (!path) {
            enqueueSnackbar('No hay pantalla de detalle para este tipo de comprobante.', { variant: 'info' });
            return;
        }

        // Nueva pestaña para no perder el borrador de la orden de pago
        window.open(path, '_blank', 'noopener,noreferrer');
    };
    const [tipoPagoSeleccionado, setTipoPagoSeleccionado] = useState('');
    const [activeStep, setActiveStep] = useState(0);
    const [indicePagoEditando, setIndicePagoEditando] = useState<number | null>(null);
    const pagoEdicionRef = React.useRef<TipoPagoData | null>(null);
    const [openProveedorSearch, setOpenProveedorSearch] = useState(false);

    const [grupoCajaSeleccionado, setGrupoCajaSeleccionado] = useState<GrupoCajaChica | null>(null);
    const [openGrupoCajaDialog, setOpenGrupoCajaDialog] = useState(false);
    const [gruposCaja, setGruposCaja] = useState<GrupoCajaChica[]>([]);
    const [grupoCajaLoading, setGrupoCajaLoading] = useState(false);
    const [grupoCajaSeleccionadoId, setGrupoCajaSeleccionadoId] = useState<number | null>(null);
    const [grupoCajaPage, setGrupoCajaPage] = useState(1);
    const [grupoCajaHasMore, setGrupoCajaHasMore] = useState(true);
    const [cuentaTransferSeleccionadaId, setCuentaTransferSeleccionadaId] = useState<string | number | null>(null);
    const [mediosTransferencia, setMediosTransferencia] = useState<MedioTransferencia[]>([]);
    const [_marcasTarjetas, setMarcasTarjetas] = useState<MarcaTarjeta[]>([]);
    const [bancos, setBancos] = useState<Banco[]>([]);
    const [openBancoDialog, setOpenBancoDialog] = useState(false);
    const [bancoSearch, setBancoSearch] = useState('');
    const [bancoSeleccionadoId, setBancoSeleccionadoId] = useState<number | null>(null);
    const [bancoPage, setBancoPage] = useState(1);
    const [bancoHasMore, setBancoHasMore] = useState(true);
    const [bancoLoading, setBancoLoading] = useState(false);
    const [mediosEfectivo, setMediosEfectivo] = useState<MedioTransferencia[]>([]);
    const [efectivoSeleccionadoId, setEfectivoSeleccionadoId] = useState<number | null>(null);
    const [mediosCheques, setMediosCheques] = useState<MedioTransferencia[]>([]);
    const [chequeSeleccionadoIdx, setChequeSeleccionadoIdx] = useState<string | null>(null);
    // Estados para cheques de cartera
    const [chequesCartera, setChequesCartera] = useState<ChequeCartera[]>([]);
    const [chequesCarteraSeleccionados, setChequesCarteraSeleccionados] = useState<Set<number>>(new Set());
    const [loadingChequesCartera, setLoadingChequesCartera] = useState(false);
    const [esChequeCartera, setEsChequeCartera] = useState(false);
    const [busquedaCheque, setBusquedaCheque] = useState('');
    const [orderBy, setOrderBy] = useState<keyof ChequeCartera | ''>('');
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    // Compañía emisora (librador) para cheque propio
    const [companiasLibrador, setCompaniasLibrador] = useState<Compania[]>([]);
    const [companiaLibradorId, setCompaniaLibradorId] = useState<string | null>(null);
    /** Leyendas de datos faltantes en la cartera del cheque propio (CBU, cuenta, etc.). */
    const [advertenciasCarteraChequePropio, setAdvertenciasCarteraChequePropio] = useState<string[]>([]);
    const [cargandoCarteraChequePropio, setCargandoCarteraChequePropio] = useState(false);

    // Al cargar cheques de cartera, marcar los traídos desde el informe
    useEffect(() => {
        if (nrosCargaDesdeCartera.length === 0) return;
        if (!openPagoDialog || !esChequeCartera) return;
        if (chequesCartera.length === 0 || loadingChequesCartera) return;
        if (preseleccionChequesOpRef.current) return;

        const disponibles = new Set(chequesCartera.map((c) => c.nroCarga));
        const aMarcar = nrosCargaDesdeCartera.filter((n) => disponibles.has(n));
        preseleccionChequesOpRef.current = true;

        if (aMarcar.length === 0) {
            enqueueSnackbar(
                'Los cheques preseleccionados no aparecieron en la cartera de la caja abierta del grupo actual.',
                { variant: 'warning' }
            );
            return;
        }
        setChequesCarteraSeleccionados(new Set(aMarcar));
        if (aMarcar.length < nrosCargaDesdeCartera.length) {
            enqueueSnackbar(
                `Se marcaron ${aMarcar.length} de ${nrosCargaDesdeCartera.length} cheques (algunos no aparecieron en esta caja).`,
                { variant: 'warning' }
            );
        }
    }, [
        nrosCargaDesdeCartera,
        openPagoDialog,
        esChequeCartera,
        chequesCartera,
        loadingChequesCartera,
        enqueueSnackbar,
    ]);

    useEffect(() => {
        if (!openPagoDialog) {
            preseleccionChequesOpRef.current = false;
        }
    }, [openPagoDialog]);

    // Función para manejar el ordenamiento
    const handleRequestSort = (property: keyof ChequeCartera) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    // Función para ordenar los cheques
    const ordenarCheques = (cheques: ChequeCartera[]): ChequeCartera[] => {
        if (!orderBy) return cheques;

        return [...cheques].sort((a, b) => {
            let aValue: any = a[orderBy];
            let bValue: any = b[orderBy];

            // Manejar valores nulos/undefined
            if (aValue == null) aValue = '';
            if (bValue == null) bValue = '';

            // Convertir a string para comparación si no son números
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return order === 'asc' ? aValue - bValue : bValue - aValue;
            }

            // Comparación de strings
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();

            if (order === 'asc') {
                return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
            } else {
                return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
            }
        });
    };

    // Función para seleccionar/deseleccionar todos los cheques filtrados
    const handleSeleccionarTodos = (chequesFiltrados: ChequeCartera[]) => {
        const todosSeleccionados = chequesFiltrados.every(cheque => 
            chequesCarteraSeleccionados.has(cheque.nroCarga)
        );

        const nuevoSet = new Set(chequesCarteraSeleccionados);
        if (todosSeleccionados) {
            // Deseleccionar todos los cheques filtrados
            chequesFiltrados.forEach(cheque => {
                nuevoSet.delete(cheque.nroCarga);
            });
        } else {
            // Seleccionar todos los cheques filtrados
            chequesFiltrados.forEach(cheque => {
                nuevoSet.add(cheque.nroCarga);
            });
        }
        setChequesCarteraSeleccionados(nuevoSet);
    };

    // Calcular el total de importes de cheques seleccionados
    const totalImporteSeleccionado = useMemo(() => {
        return chequesCartera
            .filter(cheque => chequesCarteraSeleccionados.has(cheque.nroCarga))
            .reduce((total, cheque) => total + (cheque.importe || 0), 0);
    }, [chequesCartera, chequesCarteraSeleccionados]);
    const [mediosTarjeta, setMediosTarjeta] = useState<MedioTransferencia[]>([]);
    const [tarjetaSeleccionadaIdx, setTarjetaSeleccionadaIdx] = useState<string | null>(null);
    const [tarjetaPage, setTarjetaPage] = useState(1);
    const [tarjetaHasMore, setTarjetaHasMore] = useState(true);
    const [tarjetaLoading, setTarjetaLoading] = useState(false);
    const [mediosOtros, setMediosOtros] = useState<MedioTransferencia[]>([]);
    const [otrosSeleccionadoIdx, setOtrosSeleccionadoIdx] = useState<string | null>(null);
    
    // Estado para controlar la solapa activa
    
    // Estados para Comprobantes de Deuda
    const [comprobantesDeuda, setComprobantesDeuda] = useState<ComprobanteDeuda[]>([]);
    const [searchComprobante, setSearchComprobante] = useState('');
    const [saldoTotal, setSaldoTotal] = useState(0);
    const [saldoVencido, setSaldoVencido] = useState(0);
    const [saldoVigente, setSaldoVigente] = useState(0);
    const [loadingComprobantes, setLoadingComprobantes] = useState(false);
    const [comprobantesSeleccionados, setComprobantesSeleccionados] = useState<Set<number>>(new Set());
    const [comprobantesExpandidos, setComprobantesExpandidos] = useState<Set<number>>(new Set());
    const [montosAplicar, setMontosAplicar] = useState<Map<number, number>>(new Map());
    const { sucursales, loading: loadingSucursales } = useSucursalesUsuarioContext();
    const [refreshComprobantesKey, setRefreshComprobantesKey] = useState(0);
    const [aplicarNcDialogOpen, setAplicarNcDialogOpen] = useState(false);
    const [aplicarNcComp, setAplicarNcComp] = useState<ComprobanteDeuda | null>(null);

    useEffect(() => {
        const raw = searchParams.get('nroProveedor');
        if (raw == null || raw === '') return;
        const num = parseInt(raw, 10);
        if (!Number.isFinite(num) || num <= 0) return;
        setFormulario((prev) => (prev.nroProveedor === num ? prev : { ...prev, nroProveedor: num }));
    }, [searchParams]);

    // Preselección de cheques desde ChequesTercerosCartera: aparecen ya como medios de pago
    useEffect(() => {
        if (chequesPrecargadosDesdeCarteraRef.current) return;

        const state = (location.state ?? {}) as {
            nrosCargaPreseleccionados?: number[];
            chequesPreseleccionados?: ChequeCartera[];
        };
        const chequesRaw = state.chequesPreseleccionados;
        const cheques = Array.isArray(chequesRaw)
            ? chequesRaw.filter((c) => Number(c?.nroCarga) > 0 && esChequeDisponibleEnCartera(c))
            : [];
        const nros = (state.nrosCargaPreseleccionados ?? [])
            .map((n) => Number(n))
            .filter((n) => Number.isFinite(n) && n > 0);

        if (cheques.length === 0 && nros.length === 0) return;

        chequesPrecargadosDesdeCarteraRef.current = true;
        const nrosFinales = cheques.length > 0 ? cheques.map((c) => c.nroCarga) : nros;
        setNrosCargaDesdeCartera(nrosFinales);
        preseleccionChequesOpRef.current = false;

        if (cheques.length > 0) {
            const nuevosPagos = cheques.map(chequeCarteraADetallePago);
            setDetallesPago(nuevosPagos);
            const nuevoTotal = calcularTotalMedios(nuevosPagos.map((p) => p.importe || 0));
            setFormulario((prev) => ({ ...prev, importeTotal: nuevoTotal }));
            enqueueSnackbar(
                `${cheques.length} cheque${cheques.length > 1 ? 's' : ''} listo${cheques.length > 1 ? 's' : ''} para endosar. Seleccioná el proveedor (podés aplicar a deudas o procesar a cuenta).`,
                { variant: 'info', autoHideDuration: 8000 }
            );
        } else {
            enqueueSnackbar(
                `${nrosFinales.length} cheque${nrosFinales.length > 1 ? 's' : ''} listo${nrosFinales.length > 1 ? 's' : ''} para endosar. Seleccioná el proveedor y agregá un pago con «Cheques de Tercero».`,
                { variant: 'info', autoHideDuration: 8000 }
            );
        }

        navigate(location.pathname + location.search, { replace: true, state: {} });
    }, [location.state, location.pathname, location.search, navigate, enqueueSnackbar]);

    
    const {
        totalMedios,
        totalAplicado,
        diferencia,
        diferenciaInsuficiente,
        diferenciaSinSaldo,
    } = useMemo(() => {
        const importesMedios = detallesPago.map((p) => p.importe || 0);
        const montos =
            comprobantesSeleccionados.size === 0
                ? []
                : Array.from(comprobantesSeleccionados).map((id) => {
                      const comp = comprobantesDeuda.find((c) => c.id === id);
                      if (!comp) return 0;
                      const montoAplicar = montosAplicar.get(id);
                      const monto =
                          montoAplicar !== undefined && montoAplicar !== null
                              ? montoAplicar
                              : montoImputableComprobante(comp);
                      return roundMoney(monto);
                  });
        return calcularTotalesTesoreria(importesMedios, montos);
    }, [detallesPago, comprobantesSeleccionados, comprobantesDeuda, montosAplicar]);

    // Determinar si el Proveedor puede ser modificado
    const ProveedorBloqueado = useMemo(() => {
        if (comprobantesSeleccionados.size > 0) return true;
        if (detallesPago.length === 0) return false;
        // Endoso desde cartera: los cheques llegan antes; el proveedor se elige después
        const soloChequesEndoso = detallesPago.every(
            (p) => (p.tipoPago || '').toLowerCase() === 'cheque' && p.nroCarga != null
        );
        if (soloChequesEndoso) return false;
        return true;
    }, [comprobantesSeleccionados.size, detallesPago]);
    const _tipoCodeFor = (tipoUI: string): string => {
        switch ((tipoUI || '').toLowerCase()) {
            case 'efectivo': return 'E';
            case 'cheque': return 'C';
            case 'chequepropio': return 'P';
            case 'tarjeta': return 'T';
            case 'transferencia': return 'TB';
            case 'otros': return 'O';
            default: return '';
        }
    };

    // Autocompletar Librador con el nombre del Proveedor cuando se selecciona Cheque
    useEffect(() => {
        if (tipoPagoSeleccionado === 'Cheque' && activeStep === 1) {
            if (!pagoActual.librador && formulario.nombre) {
                setPagoActual(prev => ({ ...prev, librador: formulario.nombre.slice(0, 25) }));
            }
        }
    }, [tipoPagoSeleccionado, activeStep, formulario.nombre, pagoActual.librador]);

    // Cheque propio: Destino = CUIT-Nombre del proveedor (como en Libro Banco)
    useEffect(() => {
        if (tipoPagoSeleccionado !== 'ChequePropio' || activeStep !== 1) return;
        const destino = [formulario.cuit, formulario.nombre].filter(Boolean).join('-');
        if (!destino) return;
        setPagoActual((prev) => {
            if (prev.detalle) return prev;
            return { ...prev, detalle: destino };
        });
    }, [tipoPagoSeleccionado, activeStep, formulario.cuit, formulario.nombre]);

    // Cheque propio: cargar compañías y preseleccionar la primera como librador
    useEffect(() => {
        if (!openPagoDialog || tipoPagoSeleccionado !== 'ChequePropio') return;
        let cancelled = false;
        (async () => {
            try {
                const list = await companiaService.getCompanias({ pageNumber: 1, pageSize: 100 });
                if (cancelled) return;
                setCompaniasLibrador(list || []);
                if ((list || []).length === 0) {
                    setCompaniaLibradorId(null);
                    return;
                }
                setCompaniaLibradorId((prev) => {
                    if (prev && list.some((c) => c.idCompania === prev)) {
                        const actual = list.find((c) => c.idCompania === prev)!;
                        const datos = libradorDesdeCompania(actual);
                        setPagoActual((p) => ({ ...p, ...datos }));
                        return prev;
                    }
                    const primera = list[0];
                    const datos = libradorDesdeCompania(primera);
                    setPagoActual((p) => ({ ...p, ...datos }));
                    return primera.idCompania;
                });
            } catch (e) {
                console.error('No se pudieron cargar las compañías', e);
                if (!cancelled) {
                    setCompaniasLibrador([]);
                    setCompaniaLibradorId(null);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [openPagoDialog, tipoPagoSeleccionado]);

    // Cargar marcas de tarjeta al seleccionar "Tarjeta"
    useEffect(() => {
        if (tipoPagoSeleccionado === 'Tarjeta' && openPagoDialog) {
            (async () => {
                try {
                    const marcas = await tesoreriaService.getMarcasTarjetas();
                    setMarcasTarjetas(marcas);
                } catch (e) {
                    console.error('No se pudieron cargar las marcas de tarjetas', e);
                    setMarcasTarjetas([]);
                }
            })();
        }
    }, [tipoPagoSeleccionado, openPagoDialog]);


    // Pre-cargar medios de transferencia usando el grupo guardado en localStorage
    useEffect(() => {
        if (openPagoDialog && tipoPagoSeleccionado === 'Transferencia') {
            try {
                const saved = localStorage.getItem('grupoCajaChicaSeleccionado');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    const codigo = parsed?.codigo;
                    if (codigo != null) {
                        setGrupoCajaSeleccionado(parsed);
                        (async () => {
                            try {
                                const medios = await tesoreriaService.getMediosTransferenciaPorGrupo(codigo);
                                setMediosTransferencia(medios);
                                setCuentaTransferSeleccionadaId(null);
                            } catch {
                                setMediosTransferencia([]);
                            }
                        })();
                    }
                }
            } catch {}
        }
    }, [openPagoDialog, tipoPagoSeleccionado]);

    // Pre-cargar medios de efectivo usando el grupo guardado
    useEffect(() => {
        if (openPagoDialog && tipoPagoSeleccionado === 'Efectivo') {
            try {
                const saved = localStorage.getItem('grupoCajaChicaSeleccionado');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    const codigo = parsed?.codigo;
                    if (codigo != null) {
                        setGrupoCajaSeleccionado(parsed);
                        (async () => {
                            try {
                                const medios = await tesoreriaService.getMediosEfectivoPorGrupo(codigo);
                                setMediosEfectivo(medios);
                                setEfectivoSeleccionadoId(null);
                            } catch {
                                setMediosEfectivo([]);
                            }
                        })();
                    }
                }
            } catch {}
        }
    }, [openPagoDialog, tipoPagoSeleccionado]);

    // Auto-seleccionar cuando solo hay una opción de efectivo
    useEffect(() => {
        if (tipoPagoSeleccionado === 'Efectivo' && mediosEfectivo.length === 1) {
            const unico = mediosEfectivo[0];
            if (unico && efectivoSeleccionadoId !== unico.id) {
                setEfectivoSeleccionadoId(unico.id);
                setPagoActual(prev => ({ ...prev, medioLabel: unico.nombre, mcp: unico.mcp || unico.nombre }));
            }
        }
    }, [tipoPagoSeleccionado, mediosEfectivo, efectivoSeleccionadoId]);

    // Transferencias: auto-seleccionar si hay una sola opción; si hay varias, no seleccionar
    useEffect(() => {
        if (tipoPagoSeleccionado === 'Transferencia') {
            if (mediosTransferencia.length === 1) {
                const unico = mediosTransferencia[0];
                if (unico && String(cuentaTransferSeleccionadaId) !== '0') {
                    setCuentaTransferSeleccionadaId('0');
                    const idNum = Number(unico.id);
                    setPagoActual(prev => ({ ...prev, nroCuenta: Number.isFinite(idNum) ? idNum : undefined, medioLabel: unico.nombre }));
                }
            } else if (mediosTransferencia.length > 1) {
                const idx = Number(cuentaTransferSeleccionadaId);
                if (
                    cuentaTransferSeleccionadaId != null &&
                    (isNaN(idx) || idx < 0 || idx >= mediosTransferencia.length)
                ) {
                    setCuentaTransferSeleccionadaId(null);
                }
            }
        }
    }, [tipoPagoSeleccionado, mediosTransferencia, cuentaTransferSeleccionadaId]);

    // Cheques (terceros / propios): cargar medios por grupo guardado al abrir diálogo
    useEffect(() => {
        if (openPagoDialog && (tipoPagoSeleccionado === 'Cheque' || tipoPagoSeleccionado === 'ChequePropio')) {
            try {
                const saved = localStorage.getItem('grupoCajaChicaSeleccionado');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    const codigo = parsed?.codigo;
                    if (codigo != null) {
                        (async () => {
                            try {
                                const medios =
                                    tipoPagoSeleccionado === 'ChequePropio'
                                        ? await tesoreriaService.getMediosChequesPropiosPorGrupo(codigo)
                                        : await tesoreriaService.getMediosChequesPorGrupo(codigo);
                                setMediosCheques(medios);
                                setChequeSeleccionadoIdx(null);
                                setEsChequeCartera(false);
                                setChequesCartera([]);
                                setChequesCarteraSeleccionados(new Set());
                            } catch {
                                setMediosCheques([]);
                            }
                        })();
                    }
                }
            } catch {}
        }
    }, [openPagoDialog, tipoPagoSeleccionado]);

    // Cargar cheques de cartera cuando se selecciona "Cheques de Tercero" como tipo de pago
    // Independientemente del medio específico seleccionado
    useEffect(() => {
        if (tipoPagoSeleccionado === 'Cheque' && chequeSeleccionadoIdx != null && openPagoDialog) {
            // Si el tipo de pago es "Cheque" (Cheques de Tercero), siempre mostrar listado de cheques de cartera
            setEsChequeCartera(true);
            setLoadingChequesCartera(true);
            try {
                const saved = localStorage.getItem('grupoCajaChicaSeleccionado');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    const numeroGrupo = parsed?.codigo ?? parsed?.id;
                    if (numeroGrupo != null) {
                        (async () => {
                            try {
                                const cajaAbierta = await tesoreriaService.getCajaAbiertaPorGrupo(numeroGrupo);
                                const codigoCaja = cajaAbierta?.codigoCaja ?? cajaAbierta?.nroCaja ?? cajaAbierta?.codigo ?? cajaAbierta?.id ?? null;

                                if (cajaAbierta && codigoCaja != null && codigoCaja > 0) {
                                    const PAGE_SIZE = 20;
                                    const MAX_PAGES = 25;
                                    let pageNumber = 1;
                                    let allCheques: ChequeCartera[] = [];
                                    let hasMore = true;

                                    while (hasMore && pageNumber <= MAX_PAGES) {
                                        const result = await tesoreriaService.getChequesCartera(codigoCaja, pageNumber, PAGE_SIZE);
                                        allCheques = [...allCheques, ...result.cheques];
                                        hasMore = result.hasMore;

                                        if (!hasMore || result.cheques.length === 0) {
                                            break;
                                        }

                                        pageNumber++;
                                        await new Promise<void>((r) => setTimeout(r, 0));
                                    }

                                    setChequesCartera(allCheques.filter(esChequeDisponibleEnCartera));
                                } else {
                                    enqueueSnackbar('No se encontró una caja abierta para este grupo', { variant: 'warning' });
                                    setChequesCartera([]);
                                }
                            } catch (error) {
                                console.error('Error al cargar cheques de cartera:', error);
                                enqueueSnackbar('Error al cargar cheques de cartera', { variant: 'error' });
                                setChequesCartera([]);
                            } finally {
                                setLoadingChequesCartera(false);
                            }
                        })();
                    } else {
                        console.warn('⚠️ NumeroGrupo es null o undefined. parsed:', parsed);
                        setLoadingChequesCartera(false);
                    }
                } else {
                    console.warn('⚠️ No hay grupoCajaChicaSeleccionado en localStorage');
                    setLoadingChequesCartera(false);
                }
            } catch (error) {
                console.error('❌ Error al parsear grupoCajaChicaSeleccionado:', error);
                setLoadingChequesCartera(false);
            }
        } else if (tipoPagoSeleccionado !== 'Cheque' || chequeSeleccionadoIdx == null) {
            setEsChequeCartera(false);
            setChequesCartera([]);
            setChequesCarteraSeleccionados(new Set());
        }
    }, [tipoPagoSeleccionado, chequeSeleccionadoIdx, openPagoDialog, enqueueSnackbar]);

    // Otros: cargar medios por grupo guardado
    useEffect(() => {
        if (openPagoDialog && tipoPagoSeleccionado === 'Otros') {
            try {
                const saved = localStorage.getItem('grupoCajaChicaSeleccionado');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    const codigo = parsed?.codigo;
                    if (codigo != null) {
                        (async () => {
                            try {
                                const medios = await tesoreriaService.getMediosOtrosPorGrupo(codigo);
                                setMediosOtros(medios);
                                setOtrosSeleccionadoIdx(null);
                            } catch {
                                setMediosOtros([]);
                            }
                        })();
                    }
                }
            } catch {}
        }
    }, [openPagoDialog, tipoPagoSeleccionado]);

    // Cheques (terceros / propios): auto-seleccionar si hay una sola opción y asegurar mcp
    useEffect(() => {
        if (
            (tipoPagoSeleccionado === 'Cheque' || tipoPagoSeleccionado === 'ChequePropio') &&
            mediosCheques.length === 1
        ) {
            setChequeSeleccionadoIdx('0');
            const unico = mediosCheques[0];
            if (unico) {
                setPagoActual(prev => ({ ...prev, medioLabel: unico.nombre, mcp: unico.mcp || unico.nombre }));
                if (tipoPagoSeleccionado === 'ChequePropio') {
                    void (async () => {
                        setCargandoCarteraChequePropio(true);
                        setAdvertenciasCarteraChequePropio([]);
                        try {
                            const bancoInfo = await bancoDesdeCarteraMcp(unico);
                            setAdvertenciasCarteraChequePropio(bancoInfo.advertencias);
                            setPagoActual((prev) => ({
                                ...prev,
                                banco: bancoInfo.banco,
                                cartera: bancoInfo.cartera > 0 ? bancoInfo.cartera : undefined,
                                nroBanco: bancoInfo.nroBanco,
                                nroSucursal: bancoInfo.nroSucursal,
                                ...(bancoInfo.ctaBancaria && !prev.ctaBancaria
                                    ? { ctaBancaria: bancoInfo.ctaBancaria }
                                    : {}),
                            }));
                        } finally {
                            setCargandoCarteraChequePropio(false);
                        }
                    })();
                }
            }
        }
    }, [tipoPagoSeleccionado, mediosCheques]);

    // Al editar un medio, re-seleccionar el medio original cuando las listas se (re)cargan
    useEffect(() => {
        const original = pagoEdicionRef.current;
        if (indicePagoEditando == null || !openPagoDialog || !original?.mcp) return;
        if (original.tipoPago !== tipoPagoSeleccionado) return;
        const coincide = (m: MedioTransferencia) => (m.mcp || m.nombre) === original.mcp || m.mcp === original.mcp;
        const idxEn = (lista: MedioTransferencia[]) => {
            const idx = lista.findIndex(coincide);
            return idx >= 0 ? String(idx) : null;
        };
        if (tipoPagoSeleccionado === 'Efectivo' && efectivoSeleccionadoId == null) {
            const medio = mediosEfectivo.find(coincide);
            if (medio) setEfectivoSeleccionadoId(medio.id);
        } else if (tipoPagoSeleccionado === 'Transferencia' && cuentaTransferSeleccionadaId == null) {
            const idx = idxEn(mediosTransferencia);
            if (idx != null) setCuentaTransferSeleccionadaId(idx);
        } else if (tipoPagoSeleccionado === 'ChequePropio' && chequeSeleccionadoIdx == null) {
            const idx = idxEn(mediosCheques);
            if (idx != null) setChequeSeleccionadoIdx(idx);
        } else if (tipoPagoSeleccionado === 'Tarjeta' && tarjetaSeleccionadaIdx == null) {
            const idx = idxEn(mediosTarjeta);
            if (idx != null) setTarjetaSeleccionadaIdx(idx);
        } else if (tipoPagoSeleccionado === 'Otros' && otrosSeleccionadoIdx == null) {
            const idx = idxEn(mediosOtros);
            if (idx != null) setOtrosSeleccionadoIdx(idx);
        }
    }, [
        indicePagoEditando,
        openPagoDialog,
        tipoPagoSeleccionado,
        mediosEfectivo,
        efectivoSeleccionadoId,
        mediosTransferencia,
        cuentaTransferSeleccionadaId,
        mediosCheques,
        chequeSeleccionadoIdx,
        mediosTarjeta,
        tarjetaSeleccionadaIdx,
        mediosOtros,
        otrosSeleccionadoIdx,
    ]);

    // Al editar un cheque propio, conservar la compañía librador original
    useEffect(() => {
        const original = pagoEdicionRef.current;
        if (indicePagoEditando == null || original?.tipoPago !== 'ChequePropio' || companiasLibrador.length === 0) return;
        const cuitOriginal = String(original.nroLibrador || '').replace(/\D/g, '');
        if (!cuitOriginal) return;
        const compania = companiasLibrador.find((c) => String(c.cuit || '').replace(/\D/g, '') === cuitOriginal);
        if (!compania) return;
        setCompaniaLibradorId(compania.idCompania);
        setPagoActual((prev) => ({ ...prev, librador: original.librador, nroLibrador: original.nroLibrador }));
    }, [indicePagoEditando, companiasLibrador]);

    // Cargar bancos en diálogo al abrir o buscar
    useEffect(() => {
        let timer: any;
        const PAGE_SIZE = 20; // Límite del backend Banco (1-20)
        const search = (bancoSearch || '').trim();
        const isNumericSearch = /^\d+$/.test(search);
        const fetchPage = async (reset: boolean) => {
            try {
                setBancoLoading(true);
                if (isNumericSearch) {
                    const allBanks = await tesoreriaService.getBancosAll();
                    const filtered = allBanks.filter((b) => String(b.nroBanco ?? '').includes(search));
                    setBancos(filtered);
                    setBancoHasMore(false);
                    setBancoPage(1);
                    return;
                }

                const pageToLoad = reset ? 1 : bancoPage;
                const items = await tesoreriaService.getBancosPaged({
                    searchTerm: bancoSearch || undefined,
                    pageNumber: pageToLoad,
                    pageSize: PAGE_SIZE,
                });
                setBancoHasMore(items.length > 0);
                setBancoPage(pageToLoad + 1);
                setBancos(prev => {
                    const next = reset ? items : [...prev, ...items];
                    const map = new Map<number, Banco>();
                    for (const b of next) if (!map.has(b.id)) map.set(b.id, b);
                    return Array.from(map.values());
                });
            } catch (e) {
                console.error('No se pudieron cargar los bancos', e);
                if (reset) setBancos([]);
            } finally {
                setBancoLoading(false);
            }
        };

        if (openBancoDialog) {
            timer = setTimeout(() => fetchPage(true), 300);
        }
        return () => timer && clearTimeout(timer);
    }, [openBancoDialog, bancoSearch]);

    const handleBancoScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
        if (/^\d+$/.test((bancoSearch || '').trim())) return;
        if (!bancoHasMore || bancoLoading) return;
        const target = e.currentTarget;
        if (target.scrollTop + target.clientHeight >= target.scrollHeight - 64) {
            const PAGE_SIZE = 20; // Límite del backend Banco (1-20)
            (async () => {
                try {
                    setBancoLoading(true);
                    const items = await tesoreriaService.getBancosPaged({
                        searchTerm: bancoSearch || undefined,
                        pageNumber: bancoPage,
                        pageSize: PAGE_SIZE,
                    });
                    setBancoHasMore(items.length > 0);
                    setBancoPage(prev => prev + 1);
                    setBancos(prev => {
                        const next = [...prev, ...items];
                        const map = new Map<number, Banco>();
                        for (const b of next) if (!map.has(b.id)) map.set(b.id, b);
                        return Array.from(map.values());
                    });
                } finally {
                    setBancoLoading(false);
                }
            })();
        }
    };

    // Tarjetas: cargar medios por grupo (tipo T) con paginado infinito
    useEffect(() => {
        if (openPagoDialog && tipoPagoSeleccionado === 'Tarjeta') {
            try {
                const saved = localStorage.getItem('grupoCajaChicaSeleccionado');
                if (saved) {
                    const parsed = JSON.parse(saved);
                    const codigo = parsed?.codigo;
                    if (codigo != null) {
                        (async () => {
                            try {
                                setTarjetaLoading(true);
                                const PAGE_SIZE = 30;
                                const medios = await tesoreriaService.getMediosTarjetaPorGrupoPaged(codigo, { pageNumber: 1, pageSize: PAGE_SIZE });
                                setMediosTarjeta(medios);
                                setTarjetaSeleccionadaIdx(null);
                                setTarjetaHasMore((medios?.length || 0) > 0);
                                setTarjetaPage(2);
                            } catch {
                                setMediosTarjeta([]);
                                setTarjetaHasMore(false);
                            } finally {
                                setTarjetaLoading(false);
                            }
                        })();
                    }
                }
            } catch {}
        }
    }, [openPagoDialog, tipoPagoSeleccionado]);

    // Tarjetas: auto-seleccionar si hay única opción y asegurar mcp
    useEffect(() => {
        if (tipoPagoSeleccionado === 'Tarjeta' && mediosTarjeta.length === 1) {
            setTarjetaSeleccionadaIdx('0');
            const unico = mediosTarjeta[0];
            if (unico) {
                const idNum = Number(unico.id);
                setPagoActual(prev => ({
                    ...prev,
                    nroCuenta: Number.isFinite(idNum) ? idNum : undefined,
                    medioLabel: unico.nombre,
                    mcp: unico.mcp || unico.nombre,
                }));
            }
        }
    }, [tipoPagoSeleccionado, mediosTarjeta]);

    const handleFormularioChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target as any;
        setFormulario(prev => ({
            ...prev,
            [name]: name === 'nroProveedor' || name === 'nroSuc' ? (Number(value) || 0) : value
        }));
    };

    const setNroSucFormulario = useCallback((nroSuc: number) => {
        setFormulario((prev) => (Number(prev.nroSuc) === nroSuc ? prev : { ...prev, nroSuc }));
    }, []);

    const { onSucursalChange } = useSucursalAltaFormulario({
        nroSuc: Number(formulario.nroSuc) || 0,
        setNroSuc: setNroSucFormulario,
        sucursales,
        loadingSucursales,
        enabled: true,
    });

    // Búsqueda automática por número de proveedor (p. ej. desde URL) cuando aún no hay nombre cargado
    useEffect(() => {
        if (formulario.nombre) return;
        const value = formulario.nroProveedor;
        const handle = setTimeout(() => {
            const nroProveedor = parseInt((value ?? 0).toString());
            if (!isNaN(nroProveedor) && nroProveedor > 0) {
                handleProveedorSearch(nroProveedor);
            }
        }, 500);
        return () => clearTimeout(handle);
    }, [formulario.nroProveedor, formulario.nombre]);

    // Cargar comprobantes de deuda cuando se selecciona un Proveedor
    useEffect(() => {
        const nroProveedor = formulario.nroProveedor;
        if (!nroProveedor || nroProveedor <= 0) {
            setComprobantesDeuda([]);
            setSaldoTotal(0);
            setSaldoVencido(0);
            setSaldoVigente(0);
            return;
        }

        const cargarComprobantes = async () => {
            setLoadingComprobantes(true);
            try {
                console.log('🔍 Cargando comprobantes para Proveedor:', nroProveedor);
                // El backend pagina (default ~10); hay que recorrer todas las páginas
                const maxPageSize = 50;
                const items: any[] = [];
                let currentPage = 1;
                let hasMore = true;

                const creditosPromise = tesoreriaAplicacionesService
                    .getCreditosProveedorConSaldoPendiente(nroProveedor)
                    .catch(() => null);

                while (hasMore) {
                    const response = await cuentaCorrienteProveedoresService.getComprobantesProveedor(nroProveedor, {
                        pageNumber: currentPage,
                        pageSize: maxPageSize,
                    });
                    items.push(...response.items);

                    if (response.items.length < maxPageSize) {
                        hasMore = false;
                    } else if (response.totalCount > 0 && items.length >= response.totalCount) {
                        hasMore = false;
                    } else {
                        currentPage++;
                    }
                }

                const creditosRes = await creditosPromise;
                let ncConSaldoPendiente: Set<number> | null = null;
                const ncSaldoPendienteByFormulario = new Map<number, number>();
                if (creditosRes != null) {
                    ncConSaldoPendiente = new Set<number>();
                    for (const cred of creditosRes) {
                        const nro = Number(cred.nroFormulario);
                        if (!Number.isFinite(nro) || nro <= 0) continue;
                        if (
                            !esNotaCreditoProveedor(
                                cred.nroTipoFormulario ?? '',
                                `${cred.letra ?? ''}${cred.nroSuc ?? ''}${cred.nroComprobante ?? ''}`
                            )
                        ) {
                            continue;
                        }
                        const pendiente = roundMoney(cred.saldoPendiente ?? 0);
                        if (pendiente <= 0) continue;
                        ncConSaldoPendiente.add(nro);
                        ncSaldoPendienteByFormulario.set(nro, pendiente);
                    }
                }

                console.log('📋 Items procesados (todas las páginas):', items.length);

                // Mapear la respuesta del API a la interfaz ComprobanteDeuda
                const comprobantes: ComprobanteDeuda[] = items.map((item: any, index: number) => {
                    // Obtener el saldo - priorizar saldo, luego saldoPendiente, luego saldoActual, y finalmente importe como fallback
                    let saldo = 0;
                    if (item.saldo !== undefined && item.saldo !== null) {
                        saldo = Number(item.saldo);
                    } else if (item.saldoPendiente !== undefined && item.saldoPendiente !== null) {
                        saldo = Number(item.saldoPendiente);
                    } else if (item.saldoActual !== undefined && item.saldoActual !== null) {
                        saldo = Number(item.saldoActual);
                    } else if (item.importe !== undefined && item.importe !== null) {
                        saldo = Number(item.importe);
                    } else if (item.monto !== undefined && item.monto !== null) {
                        saldo = Number(item.monto);
                    } else if (item.total !== undefined && item.total !== null) {
                        saldo = Number(item.total);
                    } else if (item.saldoDeb !== undefined || item.saldoHab !== undefined) {
                        saldo = Number(item.saldoDeb ?? 0) - Number(item.saldoHab ?? 0);
                    }
                    
                    // Obtener el importe
                    let importe = 0;
                    if (item.importe !== undefined && item.importe !== null) {
                        importe = Number(item.importe);
                    } else if (item.monto !== undefined && item.monto !== null) {
                        importe = Number(item.monto);
                    } else if (item.total !== undefined && item.total !== null) {
                        importe = Number(item.total);
                    } else {
                        importe = saldo; // Si no hay importe, usar el saldo
                    }
                    
                    // Obtener fecha de vencimiento - probar múltiples variantes del campo
                    let fechaVencimientoStr = item.fechaVencimiento || 
                                             item.fechaVenc || 
                                             item.fechaVto || 
                                             item.fecha_vencimiento ||
                                             item.fecha_venc ||
                                             item.fecha_vto ||
                                             null;
                    
                    console.log('📅 Fecha de vencimiento obtenida del API:', {
                        fechaVencimiento: item.fechaVencimiento,
                        fechaVenc: item.fechaVenc,
                        fechaVto: item.fechaVto,
                        fecha_vencimiento: item.fecha_vencimiento,
                        fecha_venc: item.fecha_venc,
                        fecha_vto: item.fecha_vto,
                        fechaVencimientoStr: fechaVencimientoStr
                    });
                    
                    // Si no hay fecha de vencimiento, usar fecha de emisión + días de plazo (si está disponible)
                    if (!fechaVencimientoStr && item.fechaEmision) {
                        fechaVencimientoStr = item.fechaEmision;
                        console.log('⚠️ No se encontró fecha de vencimiento, usando fecha de emisión:', fechaVencimientoStr);
                    }
                    
                    // Si aún no hay fecha, usar fecha actual como fallback
                    if (!fechaVencimientoStr) {
                        fechaVencimientoStr = new Date().toISOString();
                        console.log('⚠️ No se encontró fecha de vencimiento ni de emisión, usando fecha actual:', fechaVencimientoStr);
                    }
                    
                    // Obtener número de comprobante - priorizar el campo "numero"
                    const nroComprobanteStr = (item.numero?.toString() || item.nroComprobante?.toString() || item.numeroComprobante?.toString() || item.comprobante?.toString() || '').trim();
                    
                    console.log('🔢 Número de comprobante obtenido del API:', {
                        numero: item.numero,
                        nroComprobante: item.nroComprobante,
                        numeroComprobante: item.numeroComprobante,
                        comprobante: item.comprobante,
                        nroComprobanteStr: nroComprobanteStr
                    });
                    
                    // Obtener tipo de comprobante - si no hay tipo, usar "compro" si está disponible
                    let tipoComprobanteStr = (item.tipoComprobante || item.tipo || item.tipoDocumento || item.descripcionTipo || '').trim();
                    if (!tipoComprobanteStr || tipoComprobanteStr === '') {
                        // Si no hay tipo, usar "compro" si está disponible
                        tipoComprobanteStr = (item.compro?.toString() || item.comprobante?.toString() || 'Sin tipo').trim();
                    }
                    
                    // Obtener nroFormulario del API
                    const nroFormulario = item.nroFormulario || item.nroIdformulario || item.formularioId || item.idFormulario || undefined;
                    
                    if (nroFormulario) {
                        console.log(`📝 Comprobante ${nroComprobanteStr} tiene nroFormulario:`, {
                            nroComprobante: nroComprobanteStr,
                            nroFormulario: nroFormulario,
                            itemNroFormulario: item.nroFormulario,
                            itemNroIdformulario: item.nroIdformulario,
                            itemFormularioId: item.formularioId,
                            itemIdFormulario: item.idFormulario
                        });
                    } else {
                        console.warn(`⚠️ Comprobante ${nroComprobanteStr} NO tiene nroFormulario. Campos disponibles:`, {
                            nroComprobante: nroComprobanteStr,
                            itemKeys: Object.keys(item),
                            itemNroFormulario: item.nroFormulario,
                            itemNroIdformulario: item.nroIdformulario,
                            itemFormularioId: item.formularioId,
                            itemIdFormulario: item.idFormulario
                        });
                    }
                    
                    const nroTipoFormulario = (item.nroTipoFormulario ?? item.NroTipoFormulario ?? '').toString().trim() || undefined;

                    const comprobante: ComprobanteDeuda = {
                        id: item.id || item.nroComprobante || item.numeroComprobante || item.comprobanteId || index + 1,
                        nroComprobante: nroComprobanteStr,
                        tipoComprobante: tipoComprobanteStr,
                        nroTipoFormulario,
                        fechaEmision: item.fechaEmision || item.fecha || item.fechaDocumento || item.fechaComprobante || new Date().toISOString(),
                        fechaVencimiento: fechaVencimientoStr,
                        importe: roundMoney(importe),
                        saldo: roundMoney(saldo),
                        vencido: false, // Se calculará después
                        nroFormulario: nroFormulario ? Number(nroFormulario) : undefined
                    };
                    
                    // Calcular si está vencido comparando la fecha de vencimiento con la fecha actual
                    let esVencido = false;
                    
                    // Primero verificar si el API ya indica que está vencido
                    if (item.vencido !== undefined && item.vencido !== null) {
                        esVencido = Boolean(item.vencido);
                        console.log(`📅 API indica vencido: ${esVencido}`);
                    } else {
                        // Si no, calcular comparando fechas
                        try {
                            // Intentar parsear la fecha de vencimiento
                            let fechaVto: Date;
                            
                            // Si es string, intentar parsearlo
                            if (typeof comprobante.fechaVencimiento === 'string') {
                                // Intentar diferentes formatos de fecha
                                const fechaStr = comprobante.fechaVencimiento.trim();
                                
                                // Si tiene formato ISO (YYYY-MM-DD o YYYY-MM-DDTHH:mm:ss)
                                if (fechaStr.includes('T') || fechaStr.match(/^\d{4}-\d{2}-\d{2}/)) {
                                    fechaVto = new Date(fechaStr);
                                } 
                                // Si tiene formato DD/MM/YYYY
                                else if (fechaStr.match(/^\d{2}\/\d{2}\/\d{4}/)) {
                                    const [dia, mes, año] = fechaStr.split('/');
                                    fechaVto = new Date(parseInt(año), parseInt(mes) - 1, parseInt(dia));
                                }
                                // Si tiene formato DD-MM-YYYY
                                else if (fechaStr.match(/^\d{2}-\d{2}-\d{4}/)) {
                                    const [dia, mes, año] = fechaStr.split('-');
                                    fechaVto = new Date(parseInt(año), parseInt(mes) - 1, parseInt(dia));
                                }
                                // Intentar parseo estándar
                                else {
                                    fechaVto = new Date(fechaStr);
                                }
                            } else {
                                fechaVto = new Date(comprobante.fechaVencimiento);
                            }
                            
                            // Validar que la fecha sea válida
                            if (isNaN(fechaVto.getTime())) {
                                console.warn('⚠️ Fecha de vencimiento inválida:', comprobante.fechaVencimiento);
                                esVencido = false;
                            } else {
                                const hoy = new Date();
                                
                                // Normalizar ambas fechas a medianoche para comparar solo fechas, no horas
                                hoy.setHours(0, 0, 0, 0);
                                fechaVto.setHours(0, 0, 0, 0);
                                
                                // Si la fecha de vencimiento es anterior o igual a hoy, está vencido
                                // IMPORTANTE: Si fechaVenc < fechaActual, entonces está vencido
                                esVencido = fechaVto < hoy;
                                
                                // Log detallado para depuración
                                const diffDias = Math.floor((hoy.getTime() - fechaVto.getTime()) / (1000 * 60 * 60 * 24));
                                console.log(`📅 Comparando fechas:`);
                                console.log(`  - Fecha Vencimiento: ${fechaVto.toLocaleDateString('es-AR')} (${fechaVto.toISOString()})`);
                                console.log(`  - Fecha Actual: ${hoy.toLocaleDateString('es-AR')} (${hoy.toISOString()})`);
                                console.log(`  - Diferencia: ${diffDias} días`);
                                console.log(`  - ¿Está vencido? ${esVencido ? '✅ SÍ' : '❌ NO'}`);
                                console.log(`  - Comparación: fechaVto (${fechaVto.getTime()}) < hoy (${hoy.getTime()}) = ${fechaVto.getTime() < hoy.getTime()}`);
                            }
                        } catch (error) {
                            console.error('❌ Error al parsear fecha de vencimiento:', error, 'Fecha:', comprobante.fechaVencimiento);
                            // Si hay error al parsear, considerar como no vencido por defecto
                            esVencido = false;
                        }
                    }
                    
                    comprobante.vencido = esVencido;
                    
                    console.log('✅ Comprobante mapeado:', {
                        ...comprobante,
                        fechaVencimientoFormateada: new Date(comprobante.fechaVencimiento).toLocaleDateString('es-AR'),
                        vencido: comprobante.vencido
                    });
                    
                    return comprobante;
                });
                
                const comprobantesFiltradosRaw = comprobantes
                    .filter((comp) => esComprobanteVisibleOrdenPago(comp, ncConSaldoPendiente))
                    .map((comp) => {
                        if (!esNcVisibleEnOrdenPago(comp, ncConSaldoPendiente)) return comp;
                        const nro = Number(comp.nroFormulario);
                        const pendiente = ncSaldoPendienteByFormulario.get(nro);
                        if (pendiente == null) return comp;
                        return { ...comp, saldo: -Math.abs(pendiente) };
                    });

                // Evitar NC duplicadas (la CC a veces trae más de una fila por el mismo nroFormulario)
                const vistosNc = new Set<number>();
                const comprobantesFiltrados = comprobantesFiltradosRaw.filter((comp) => {
                    if (!esNcVisibleEnOrdenPago(comp, ncConSaldoPendiente)) return true;
                    const nro = Number(comp.nroFormulario);
                    if (!Number.isFinite(nro) || nro <= 0) return true;
                    if (vistosNc.has(nro)) return false;
                    vistosNc.add(nro);
                    return true;
                });

                
                // Verificar que todos los comprobantes tengan el campo vencido correctamente calculado
                comprobantesFiltrados.forEach((comp, idx) => {
                    console.log(`  Comprobante ${idx + 1}:`, {
                        id: comp.id,
                        nroComprobante: comp.nroComprobante,
                        fechaVencimiento: comp.fechaVencimiento,
                        fechaVencimientoFormateada: new Date(comp.fechaVencimiento).toLocaleDateString('es-AR'),
                        vencido: comp.vencido,
                        saldo: comp.saldo
                    });
                });
                
                setComprobantesDeuda(comprobantesFiltrados);
                
                // Calcular saldos solo con débitos imputables
                const debitos = comprobantesFiltrados.filter((c) => esComprobanteImputableOrdenPago(c));
                const total = debitos.reduce((sum, c) => sum + (c.saldo || 0), 0);
                
                // Filtrar comprobantes vencidos y sumar sus saldos
                const comprobantesVencidos = debitos.filter(c => c.vencido === true);
                const vencido = comprobantesVencidos.reduce((sum, c) => sum + (c.saldo || 0), 0);
                
                // Filtrar comprobantes vigentes (no vencidos) y sumar sus saldos
                const comprobantesVigentes = debitos.filter(c => c.vencido === false);
                const vigente = comprobantesVigentes.reduce((sum, c) => sum + (c.saldo || 0), 0);
                
                console.log('💰 Saldos calculados:');
                console.log('  - Total comprobantes:', comprobantes.length);
                console.log('  - Comprobantes vencidos:', comprobantesVencidos.length);
                console.log('  - Comprobantes vigentes:', comprobantesVigentes.length);
                console.log('  - Saldo Total:', total);
                console.log('  - Saldo Vencido:', vencido);
                console.log('  - Saldo Vigente:', vigente);
                console.log('  - Verificación (Total = Vencido + Vigente):', total === (vencido + vigente) ? '✅ Correcto' : '❌ Error');
                
                setSaldoTotal(total);
                setSaldoVencido(vencido);
                setSaldoVigente(vigente);
            } catch (error: any) {
                console.error('❌ Error al cargar comprobantes de deuda:', error);
                console.error('❌ Detalles del error:', error.response?.data || error.message);
                enqueueSnackbar(
                    error.response?.data?.message || 
                    `Error al cargar comprobantes de deuda: ${error.message}` || 
                    'Error al cargar comprobantes de deuda', 
                    { variant: 'error' }
                );
                setComprobantesDeuda([]);
                setSaldoTotal(0);
                setSaldoVencido(0);
                setSaldoVigente(0);
            } finally {
                setLoadingComprobantes(false);
            }
        };

        cargarComprobantes();
    }, [formulario.nroProveedor, enqueueSnackbar, refreshComprobantesKey]);

    // Funciones de pago (comentadas temporalmente)
    // const handlePagoChange = (index: number, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
    //     const { name, value } = e.target;
    //     const newPagos = [...detallesPago];
    //     newPagos[index] = {
    //         ...newPagos[index],
    //         [name]: name === 'importe' ? Number(value) || 0 : value
    //     };
    //     setDetallesPago(newPagos);

    //     // Actualizar el importe total
    //     const nuevoTotal = newPagos.reduce((sum, pago) => sum + (pago.importe || 0), 0);
    //     setFormulario(prev => ({
    //         ...prev,
    //         importeTotal: nuevoTotal
    //     }));
    // };

    // const addPago = () => {
    //     setDetallesPago(prev => [...prev, { ...initialPago }]);
    // };

    const removePago = (index: number) => {
        setDetallesPago(prev => {
            const newPagos = prev.filter((_, i) => i !== index);
            // Actualizar el importe total
            const nuevoTotal = calcularTotalMedios(newPagos.map((pago) => pago.importe || 0));
            setFormulario(prevForm => ({
                ...prevForm,
                importeTotal: nuevoTotal
            }));
            return newPagos;
        });
    };

    const handleOpenPagoDialog = () => {
        const nroCli = parseInt((formulario.nroProveedor ?? 0).toString());
        if (!Number.isFinite(nroCli) || nroCli <= 0) {
            enqueueSnackbar('Debe seleccionar un Proveedor antes de agregar un pago', { variant: 'warning' });
            return;
        }
        
        const importeInicial = calcularImporteSugeridoPago(diferencia, diferenciaInsuficiente);
        
        setPagoActual({
            ...initialPago,
            importe: importeInicial
        });
        setTipoPagoSeleccionado('');
        setActiveStep(0);
        setIndicePagoEditando(null);
        pagoEdicionRef.current = null;
        setOpenPagoDialog(true);
        // Reset selecciones previas de medios
        setCuentaTransferSeleccionadaId(null);
        setMediosTransferencia([]);
        setEfectivoSeleccionadoId(null);
        setMediosEfectivo([]);
    };

    const handleClosePagoDialog = () => {
        setOpenPagoDialog(false);
        setPagoActual(initialPago);
        setTipoPagoSeleccionado('');
        setActiveStep(0);
        // Limpiar estados de cheques de cartera
        setEsChequeCartera(false);
        setChequesCartera([]);
        setChequesCarteraSeleccionados(new Set());
        setChequeSeleccionadoIdx(null);
        setBusquedaCheque('');
        setCompaniasLibrador([]);
        setCompaniaLibradorId(null);
        setAdvertenciasCarteraChequePropio([]);
        setCargandoCarteraChequePropio(false);
        setIndicePagoEditando(null);
        pagoEdicionRef.current = null;
    };

    const handleEditarPago = async (index: number) => {
        const pago = detallesPago[index];
        if (!pago) return;
        pagoEdicionRef.current = pago;
        setPagoActual({ ...pago });
        setIndicePagoEditando(index);
        setTipoPagoSeleccionado(pago.tipoPago);
        setEfectivoSeleccionadoId(null);
        setCuentaTransferSeleccionadaId(null);
        setChequeSeleccionadoIdx(null);
        setTarjetaSeleccionadaIdx(null);
        setOtrosSeleccionadoIdx(null);
        setEsChequeCartera(false);
        setChequesCartera([]);
        setChequesCarteraSeleccionados(new Set());
        setAdvertenciasCarteraChequePropio([]);
        setCargandoCarteraChequePropio(false);
        setActiveStep(1);
        setOpenPagoDialog(true);
        if (grupoCajaSeleccionado?.codigo) {
            try {
                await cargarMediosPorGrupoYTipo(grupoCajaSeleccionado, pago.tipoPago);
            } catch (e) {
                console.error('No se pudieron cargar los medios del grupo seleccionado', e);
                enqueueSnackbar('Error al cargar medios del grupo de caja', { variant: 'error' });
            }
        }
    };

    const handleGrupoCajaChange = (grupo: GrupoCajaChica | null) => {
        if (detallesPago.length > 0) return;
        setGrupoCajaSeleccionado(grupo);
        setGrupoCajaSeleccionadoId(grupo?.id ?? null);
        setPagoActual((prev) => ({ ...prev, grupoMcp: grupo?.codigo }));
    };

    const grupoCajaBloqueado = detallesPago.length > 0;

    const cargarMediosPorGrupoYTipo = async (grupo: GrupoCajaChica, tipo: string) => {
        if (!grupo.codigo) return;
        if (tipo === 'Transferencia') {
            const medios = await tesoreriaService.getMediosTransferenciaPorGrupo(grupo.codigo);
            setMediosTransferencia(medios);
            setCuentaTransferSeleccionadaId(null);
        } else if (tipo === 'Efectivo') {
            const medios = await tesoreriaService.getMediosEfectivoPorGrupo(grupo.codigo);
            setMediosEfectivo(medios);
            setEfectivoSeleccionadoId(null);
        } else if (tipo === 'Cheque') {
            const medios = await tesoreriaService.getMediosChequesPorGrupo(grupo.codigo);
            setMediosCheques(medios);
            setChequeSeleccionadoIdx(null);
        } else if (tipo === 'ChequePropio') {
            const medios = await tesoreriaService.getMediosChequesPropiosPorGrupo(grupo.codigo);
            setMediosCheques(medios);
            setChequeSeleccionadoIdx(null);
        } else if (tipo === 'Tarjeta') {
            const PAGE_SIZE = 30;
            const medios = await tesoreriaService.getMediosTarjetaPorGrupoPaged(grupo.codigo, { pageNumber: 1, pageSize: PAGE_SIZE });
            setMediosTarjeta(medios);
            setTarjetaSeleccionadaIdx(null);
            setTarjetaHasMore((medios?.length || 0) > 0);
            setTarjetaPage(2);
        } else if (tipo === 'Otros') {
            const medios = await tesoreriaService.getMediosOtrosPorGrupo(grupo.codigo);
            setMediosOtros(medios);
            setOtrosSeleccionadoIdx(null);
        }
    };

    const ensureGrupoCajaSeleccionado = async (): Promise<boolean> => {
        if (grupoCajaSeleccionado?.codigo) {
            setPagoActual((prev) => ({ ...prev, grupoMcp: grupoCajaSeleccionado.codigo }));
            try {
                await cargarMediosPorGrupoYTipo(grupoCajaSeleccionado, tipoPagoSeleccionado);
            } catch (e) {
                console.error('No se pudieron cargar los medios del grupo seleccionado', e);
                enqueueSnackbar('Error al cargar medios del grupo de caja', { variant: 'error' });
                return false;
            }
            setActiveStep(1);
            return true;
        }

        try {
            setGrupoCajaLoading(true);
            const grupos = await tesoreriaService.getGruposCajaChica({ usuario: loggedUsername });
            const found = findGrupoCajaGuardado(grupos || []);
            if (found) {
                setGrupoCajaSeleccionadoId(found.id);
                setGrupoCajaSeleccionado(found);
                setPagoActual((prev) => ({ ...prev, grupoMcp: found.codigo }));
                await cargarMediosPorGrupoYTipo(found, tipoPagoSeleccionado);
                setActiveStep(1);
                return true;
            }
        } catch (err) {
            console.error('Error al cargar grupos por usuario', err);
            enqueueSnackbar('Error al cargar grupos de caja', { variant: 'error' });
        } finally {
            setGrupoCajaLoading(false);
        }

        setOpenGrupoCajaDialog(true);
        return false;
    };

    useEffect(() => {
        if (!openGrupoCajaDialog) return;
        const loadFirstPage = async () => {
            try {
                setGrupoCajaLoading(true);
                const PAGE_SIZE = 30;
                const data = await tesoreriaService.getGruposCajaChicaPaged({ usuario: loggedUsername, pageNumber: 1, pageSize: PAGE_SIZE });
                setGruposCaja(data || []);
                setGrupoCajaHasMore((data?.length || 0) > 0);
                setGrupoCajaPage(2);
                if (grupoCajaSeleccionado?.id) {
                    setGrupoCajaSeleccionadoId(grupoCajaSeleccionado.id);
                }
            } catch (err: unknown) {
                console.error('Error al cargar grupos de caja chica', err);
                enqueueSnackbar('Error al cargar grupos de caja chica', { variant: 'error' });
                setGruposCaja([]);
                setGrupoCajaHasMore(false);
            } finally {
                setGrupoCajaLoading(false);
            }
        };
        loadFirstPage();
    }, [openGrupoCajaDialog, loggedUsername, enqueueSnackbar, grupoCajaSeleccionado?.id]);

    const handleGrupoCajaScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
        if (!grupoCajaHasMore || grupoCajaLoading) return;
        const target = e.currentTarget;
        if (target.scrollTop + target.clientHeight >= target.scrollHeight - 64) {
            const PAGE_SIZE = 30;
            void (async () => {
                try {
                    setGrupoCajaLoading(true);
                    const data = await tesoreriaService.getGruposCajaChicaPaged({ usuario: loggedUsername, pageNumber: grupoCajaPage, pageSize: PAGE_SIZE });
                    const existingIds = new Set(gruposCaja.map((g) => g.id));
                    const nuevos = (data || []).filter((g) => !existingIds.has(g.id));
                    setGrupoCajaHasMore(nuevos.length > 0);
                    if (nuevos.length > 0) {
                        setGrupoCajaPage((prev) => prev + 1);
                        setGruposCaja((prev) => [...prev, ...nuevos]);
                    }
                } finally {
                    setGrupoCajaLoading(false);
                }
            })();
        }
    };

    const handleConfirmarGrupoCaja = async () => {
        if (grupoCajaSeleccionadoId == null) return;
        const selected = gruposCaja.find((g) => g.id === grupoCajaSeleccionadoId);
        if (!selected) return;
        persistGrupoCajaChica(selected);
        setGrupoCajaSeleccionado(selected);
        setPagoActual((prev) => ({ ...prev, grupoMcp: selected.codigo }));
        setOpenGrupoCajaDialog(false);
        setActiveStep(1);
        try {
            await cargarMediosPorGrupoYTipo(selected, tipoPagoSeleccionado);
        } catch (e) {
            console.error('No se pudieron cargar los medios del grupo seleccionado', e);
        }
    };

    const handleTipoPagoChange = async (value: string) => {
        setTipoPagoSeleccionado(value);
        setPagoActual(prev => ({
            ...prev,
            tipoPago: value
        }));

        // Al cambiar tipo, limpiar selección de medios específicos
        if (value === 'Transferencia') {
            setCuentaTransferSeleccionadaId(null);
        }
        if (value === 'Efectivo') {
            setEfectivoSeleccionadoId(null);
        }
        if (value === 'Cheque' || value === 'ChequePropio') {
            setChequeSeleccionadoIdx(null);
            setEsChequeCartera(false);
            setChequesCartera([]);
            setChequesCarteraSeleccionados(new Set());
        }
        setAdvertenciasCarteraChequePropio([]);
        setCargandoCarteraChequePropio(false);

        // Para cualquier medio, exigir selección de Grupo de Caja
        await ensureGrupoCajaSeleccionado();
    };

    const handlePagoFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setPagoActual(prev => ({
            ...prev,
            [name]:
                name === 'importe'
                    ? (value === '' ? 0 : roundMoney(Number(value)))
                    : (name === 'nroComprobanteTa' || name === 'codigoMarca' || name === 'nroSucursal' || name === 'nroBanco')
                        ? (Number(value) || 0)
                        : value
        }));
    };

    const handleGuardarPago = () => {
        // Validar que si es cheque de cartera, se haya seleccionado al menos uno
        if (esChequeCartera && chequesCarteraSeleccionados.size === 0) {
            enqueueSnackbar('Debe seleccionar al menos un cheque de cartera', { variant: 'warning' });
            return;
        }

        if ((tipoPagoSeleccionado || '').toLowerCase() === 'chequepropio') {
            if (cargandoCarteraChequePropio) {
                enqueueSnackbar('Esperá a que termine de verificar la cartera', { variant: 'info' });
                return;
            }
            const nroBanco = Number(pagoActual.nroBanco);
            if (!Number.isFinite(nroBanco) || nroBanco < 1 || nroBanco > 999) {
                enqueueSnackbar(
                    'No se puede agregar el cheque propio: falta el N° Banco (1–999). Completá el CBU en la ficha de cartera.',
                    { variant: 'error' }
                );
                return;
            }
            const nroSucursal = Number(pagoActual.nroSucursal);
            if (!Number.isFinite(nroSucursal) || nroSucursal < 1 || nroSucursal > 999) {
                enqueueSnackbar(
                    'No se puede agregar el cheque propio: falta el N° Sucursal (1–999). Verificá el CBU / sucursal de la cartera.',
                    { variant: 'error' }
                );
                return;
            }
        }

        // Si es Efectivo/Cheque/Tarjeta y falta mcp, derivarlo del medio seleccionado
        let pagoAAgregar = { ...pagoActual } as any;
        pagoAAgregar.importe = truncToDecimals(pagoAAgregar.importe);
        if ((tipoPagoSeleccionado || '').toLowerCase() === 'efectivo') {
            if (!pagoAAgregar.mcp) {
                if (efectivoSeleccionadoId !== null) {
                    const medioSel = mediosEfectivo.find(m => m.id === efectivoSeleccionadoId);
                    if (medioSel) {
                        pagoAAgregar.mcp = medioSel?.mcp || medioSel?.nombre || '';
                    }
                } else if (mediosEfectivo.length === 1) {
                    const medioSel = mediosEfectivo[0];
                    pagoAAgregar.mcp = medioSel?.mcp || medioSel?.nombre || '';
                }
            }
        }
        if ((tipoPagoSeleccionado || '').toLowerCase() === 'cheque' || (tipoPagoSeleccionado || '').toLowerCase() === 'chequepropio') {
            if (!pagoAAgregar.mcp) {
                const idx = Number(chequeSeleccionadoIdx);
                if (!isNaN(idx) && idx >= 0 && idx < mediosCheques.length) {
                    const medioSel = mediosCheques[idx];
                    pagoAAgregar.mcp = medioSel?.mcp || medioSel?.nombre || '';
                } else if (mediosCheques.length === 1) {
                    const medioSel = mediosCheques[0];
                    pagoAAgregar.mcp = medioSel?.mcp || medioSel?.nombre || '';
                }
            }
            // Si es cheque de cartera, agregar un pago por cada cheque seleccionado
            if (esChequeCartera && chequesCarteraSeleccionados.size > 0) {
                const chequesSeleccionados = chequesCartera.filter(c => chequesCarteraSeleccionados.has(c.nroCarga));
                const nrosYaCargados = new Set(
                    detallesPago.filter((p) => p.nroCarga != null).map((p) => p.nroCarga as number)
                );
                const chequesNuevos = chequesSeleccionados.filter((c) => !nrosYaCargados.has(c.nroCarga));
                if (chequesNuevos.length === 0) {
                    enqueueSnackbar('Esos cheques ya estánan como medios de pago.', { variant: 'info' });
                    handleClosePagoDialog();
                    return;
                }
                const nuevosPagos: TipoPagoData[] = chequesNuevos.map(chequeCarteraADetallePago);
                
                setDetallesPago(prev => {
                    const actualizados = [...prev, ...nuevosPagos];
                    const nuevoTotal = calcularTotalMedios(actualizados.map((pago) => pago.importe || 0));
                    setFormulario(prevForm => ({
                        ...prevForm,
                        importeTotal: nuevoTotal
                    }));
                    return actualizados;
                });
                setNrosCargaDesdeCartera([]);
                preseleccionChequesOpRef.current = false;
                handleClosePagoDialog();
                return;
            }
        }
        if ((tipoPagoSeleccionado || '').toLowerCase() === 'tarjeta') {
            if (!pagoAAgregar.mcp) {
                const idx = Number(tarjetaSeleccionadaIdx);
                if (!isNaN(idx) && idx >= 0 && idx < mediosTarjeta.length) {
                    const medioSel = mediosTarjeta[idx];
                    pagoAAgregar.mcp = medioSel?.mcp || medioSel?.nombre || '';
                } else if (mediosTarjeta.length === 1) {
                    const medioSel = mediosTarjeta[0];
                    pagoAAgregar.mcp = medioSel?.mcp || medioSel?.nombre || '';
                }
            }
        }

        pagoAAgregar.importe = normalizeImporteMedio(Number(pagoAAgregar.importe) || 0);

        setDetallesPago(prev => {
            const actualizados = indicePagoEditando != null
                ? prev.map((p, i) => (i === indicePagoEditando ? pagoAAgregar : p))
                : [...prev, pagoAAgregar];
            const nuevoTotal = calcularTotalMedios(actualizados.map((pago) => pago.importe || 0));
            setFormulario(prevForm => ({
                ...prevForm,
                importeTotal: nuevoTotal
            }));
            return actualizados;
        });
        handleClosePagoDialog();
    };

    const handleProveedorSelect = async (Proveedor: any) => {
        // Verificar si el Proveedor está bloqueado
        if (ProveedorBloqueado) {
            enqueueSnackbar('No se puede modificar el Proveedor cuando hay comprobantes aplicados o Medios de Pago asignados', { variant: 'warning' });
            setOpenProveedorSearch(false);
            return;
        }
        
        console.log('👤 Proveedor seleccionado:', Proveedor);
        const notasPublicas = await resolverNotasPublicasProveedor(Proveedor);
        setFormulario(prev => ({
            ...prev,
            nroProveedor: Proveedor.nroProveedor,
            nombre: Proveedor.nombre,
            cuit: Proveedor.cuit || '',
            tipoIVA: Proveedor.tipoIva || '',
            descripcion: notasPublicas
        }));
        setOpenProveedorSearch(false);
        enqueueSnackbar(`Proveedor seleccionado: ${Proveedor.nombre}`, { variant: 'success' });
    };

    const handleProveedorClear = () => {
        if (ProveedorBloqueado) {
            enqueueSnackbar('No se puede quitar el proveedor cuando hay comprobantes aplicados o medios de pago asignados', { variant: 'warning' });
            return;
        }
        setFormulario((prev) => ({
            ...prev,
            nroProveedor: 0,
            nombre: '',
            cuit: '',
            tipoIVA: '',
            descripcion: '',
        }));
    };

    const resolverNotasPublicasProveedor = async (proveedor: {
        nroProveedor?: number;
        notas2?: string | null;
        Notas2?: string | null;
    }): Promise<string> => {
        if (proveedor && ('notas2' in proveedor || 'Notas2' in proveedor)) {
            return textoNotasPublicasProveedor(proveedor.notas2 ?? proveedor.Notas2);
        }
        const nro = Number(proveedor?.nroProveedor);
        if (!Number.isFinite(nro) || nro <= 0) return '';
        try {
            const completo = await proveedorService.getProveedor(nro);
            return textoNotasPublicasProveedor(completo.notas2);
        } catch {
            return '';
        }
    };

    const handleProveedorSearch = async (nroProveedor: number) => {
        try {
            console.log('🔍 Buscando Proveedor por número:', nroProveedor);
            const Proveedor = await proveedorService.getProveedor(nroProveedor);
            console.log('✅ Proveedor encontrado:', Proveedor);
            
            setFormulario(prev => ({
                ...prev,
                nroProveedor: Proveedor.nroProveedor,
                nombre: Proveedor.nombre,
                cuit: Proveedor.cuit || '',
                tipoIVA: Proveedor.tipoIva || '',
                descripcion: textoNotasPublicasProveedor(Proveedor.notas2)
            }));
            
            enqueueSnackbar(`Proveedor encontrado: ${Proveedor.nombre}`, { variant: 'success' });
        } catch (err: any) {
            console.error('❌ Error al buscar Proveedor:', err);
            enqueueSnackbar(`No se encontró el Proveedor con número ${nroProveedor}`, { variant: 'error' });
        }
    };

    const handlePagarTodo = () => {
        const debitos = comprobantesDeuda.filter((comp) => esComprobanteImputableOrdenPago(comp));
        if (debitos.length === 0 || saldoTotal === 0) {
            enqueueSnackbar('No hay comprobantes para pagar', { variant: 'warning' });
            return;
        }

        const todosLosIds = new Set(debitos.map(comp => comp.id));
        setComprobantesSeleccionados(todosLosIds);
        
        // Inicializar los montos a aplicar con el saldo de cada comprobante
        const nuevosMontos = new Map<number, number>();
        debitos.forEach(comp => {
            nuevosMontos.set(comp.id, montoImputableComprobante(comp));
        });
        setMontosAplicar(nuevosMontos);
        
        const cantidadSeleccionada = todosLosIds.size;
        enqueueSnackbar(`${cantidadSeleccionada} comprobante${cantidadSeleccionada > 1 ? 's' : ''} seleccionado${cantidadSeleccionada > 1 ? 's' : ''}`, { variant: 'success' });
    };

    const handleToggleComprobante = (id: number) => {
        const compTarget = comprobantesDeuda.find(c => c.id === id);
        if (compTarget && !esComprobanteImputableOrdenPago(compTarget)) {
            return;
        }
        setComprobantesSeleccionados(prev => {
            const nuevo = new Set(prev);
            if (nuevo.has(id)) {
                nuevo.delete(id);
                // Eliminar el monto a aplicar cuando se deselecciona
                setMontosAplicar(prevMap => {
                    const nuevoMap = new Map(prevMap);
                    nuevoMap.delete(id);
                    return nuevoMap;
                });
            } else {
                nuevo.add(id);
                // Inicializar el monto a aplicar con el saldo del comprobante
                const comp = comprobantesDeuda.find(c => c.id === id);
                if (comp) {
                    setMontosAplicar(prevMap => {
                        const nuevoMap = new Map(prevMap);
                        nuevoMap.set(id, montoImputableComprobante(comp));
                        return nuevoMap;
                    });
                }
            }
            return nuevo;
        });
    };

    const handleAbrirAplicarNc = (comp: ComprobanteDeuda) => {
        if (!comp.nroFormulario) {
            enqueueSnackbar('La nota de crédito no tiene número de formulario', { variant: 'warning' });
            return;
        }
        setAplicarNcComp(comp);
        setAplicarNcDialogOpen(true);
    };

    const handleCloseAplicarNcDialog = () => {
        setAplicarNcDialogOpen(false);
        setAplicarNcComp(null);
    };

    const handleToggleDetalles = (id: number) => {
        setComprobantesExpandidos(prev => {
            const nuevo = new Set(prev);
            if (nuevo.has(id)) {
                nuevo.delete(id);
            } else {
                nuevo.add(id);
            }
            return nuevo;
        });
    };

    const formatNumeroComprobante = (nroComprobante: string, tipoComprobante: string): string => {
        // Si el número ya tiene el formato correcto (ej: "FCB-0013-00004558"), devolverlo tal cual
        if (nroComprobante.includes('-') && nroComprobante.split('-').length >= 3) {
            return nroComprobante;
        }
        
        // Intentar formatear como "FCB-0013-00004558" basado en el tipo y número
        // Si el tipo tiene un código numérico al inicio (ej: "1 - Factura"), extraerlo
        const tipoMatch = tipoComprobante.match(/^(\d+)\s*-\s*(.+)/);
        let tipoCode = '';
        
        if (tipoMatch) {
            // Si tiene formato "1 - Factura", usar las primeras letras del nombre
            const nombreTipo = tipoMatch[2];
            // Tomar las primeras 3 letras del nombre del tipo
            tipoCode = nombreTipo.substring(0, 3).toUpperCase().replace(/\s/g, '');
            // Si no tiene suficientes letras, completar con las primeras letras del tipo completo
            if (tipoCode.length < 3) {
                tipoCode = tipoComprobante.substring(0, 3).toUpperCase().replace(/\s/g, '');
            }
        } else {
            // Si no, usar las primeras letras del tipo completo
            tipoCode = tipoComprobante.substring(0, 3).toUpperCase().replace(/\s/g, '');
        }
        
        // Si el tipoCode está vacío o es muy corto, usar un código por defecto
        if (!tipoCode || tipoCode.length < 2) {
            tipoCode = 'FCB'; // Código por defecto
        }
        
        // Extraer números del comprobante
        const numeros = nroComprobante.replace(/\D/g, '');
        
        if (numeros.length >= 12) {
            // Si tiene 12 o más dígitos, formatear como "FCB-0013-00004558"
            const parte1 = numeros.substring(0, 4).padStart(4, '0');
            const parte2 = numeros.substring(4, 12).padStart(8, '0');
            return `${tipoCode}-${parte1}-${parte2}`;
        } else if (numeros.length >= 8) {
            // Si tiene 8 o más dígitos, dividir en dos partes
            const parte1 = numeros.substring(0, 4).padStart(4, '0');
            const parte2 = numeros.substring(4).padStart(8, '0');
            return `${tipoCode}-${parte1}-${parte2}`;
        } else if (numeros.length >= 4) {
            // Si tiene 4 o más dígitos, usar formato con dos partes
            const parte1 = numeros.substring(0, 4).padStart(4, '0');
            const parte2 = numeros.substring(4).padStart(8, '0');
            return `${tipoCode}-${parte1}-${parte2}`;
        } else if (numeros.length > 0) {
            // Si tiene menos de 4 dígitos, usar formato simple
            return `${tipoCode}-${numeros.padStart(4, '0')}`;
        }
        
        // Si no hay números, devolver el formato original con el código de tipo
        return `${tipoCode}-${nroComprobante}`;
    };

    const renderCamposPago = () => {
        switch (tipoPagoSeleccionado) {
            case 'Efectivo':
                return (
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                EFECTIVO - Seleccione el medio específico:
                            </Typography>
                            <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                    {mediosEfectivo.map((medio) => (
                                        <Grid item xs={12} key={medio.id}>
                                            <Card
                                                variant={efectivoSeleccionadoId === medio.id ? 'elevation' : 'outlined'}
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    setEfectivoSeleccionadoId(medio.id);
                                                    setPagoActual(prev => ({ ...prev, medioLabel: medio.nombre, mcp: medio.mcp || medio.nombre }));
                                                }}
                                            >
                                                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <input
                                                        type="radio"
                                                        checked={efectivoSeleccionadoId === medio.id}
                                                        onChange={() => {
                                                            setEfectivoSeleccionadoId(medio.id);
                                                            setPagoActual(prev => ({ ...prev, medioLabel: medio.nombre, mcp: medio.mcp || medio.nombre }));
                                                        }}
                                                        style={{ marginRight: 8 }}
                                                    />
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                        {medio.descripcion && (
                                                            <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                        )}
                                                        {medio.clase && (
                                                            <Typography variant="caption">{medio.clase}</Typography>
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                    {mediosEfectivo.length === 0 && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2">No hay medios de efectivo disponibles para este grupo.</Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <CurrencyTextField
                                fullWidth
                                label="Monto"
                                allowNegative
                                value={pagoActual.importe ?? 0}
                                onChange={(v) => setPagoActual((prev) => ({ ...prev, importe: v }))}
                                required
                            />
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                Use un importe negativo para registrar vuelto en efectivo (debe haber otros medios que compensen el total a aplicar).
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Observaciones (opcional)"
                                name="detalle"
                                value={pagoActual.detalle}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>
                    </Grid>
                );

            case 'Transferencia':
                return (
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                TRANSFERENCIAS - Seleccione el medio específico:
                            </Typography>
                            <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                <RadioGroup
                                    name="transferOption"
                                    value={cuentaTransferSeleccionadaId != null ? String(cuentaTransferSeleccionadaId) : ''}
                                    onChange={(e) => {
                                        const newId = String(e.target.value);
                                        setCuentaTransferSeleccionadaId(newId);
                                        const idx = Number(newId);
                                        if (!isNaN(idx) && idx >= 0 && idx < mediosTransferencia.length) {
                                            const medioSel = mediosTransferencia[idx];
                                            const idNum = Number(medioSel.id);
                                            setPagoActual(prev => ({ ...prev, nroCuenta: Number.isFinite(idNum) ? idNum : undefined, medioLabel: medioSel.nombre, mcp: medioSel.mcp || medioSel.nombre }));
                                        }
                                    }}
                                >
                                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                        {mediosTransferencia.map((medio, idx) => (
                                            <Grid item xs={12} key={medio.id ?? idx}>
                                                <Card variant={(String(cuentaTransferSeleccionadaId) === String(idx)) ? 'elevation' : 'outlined'}>
                                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <FormControlLabel
                                                            value={String(idx)}
                                                            control={<Radio />}
                                                            label={
                                                                <Box>
                                                                    <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                                    {medio.descripcion && (
                                                                        <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                                    )}
                                                                    {medio.clase && (
                                                                        <Typography variant="caption">{medio.clase}</Typography>
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{ m: 0 }}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                        {mediosTransferencia.length === 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">No hay medios de transferencia disponibles para este grupo.</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </RadioGroup>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <CurrencyTextField
                                fullWidth
                                label="Monto"
                                value={pagoActual.importe ?? 0}
                                onChange={(v) => setPagoActual((prev) => ({ ...prev, importe: v }))}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Observaciones (opcional)"
                                name="detalle"
                                value={pagoActual.detalle}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>
                    </Grid>
                );

            case 'Tarjeta':
                return (
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                TARJETAS - Seleccione el medio específico:
                            </Typography>
                            <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}
                                onScroll={(e) => {
                                    if (!tarjetaHasMore || tarjetaLoading) return;
                                    const target = e.currentTarget;
                                    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 64) {
                                        const saved = localStorage.getItem('grupoCajaChicaSeleccionado');
                                        if (!saved) return;
                                        const parsed = JSON.parse(saved);
                                        const codigo = parsed?.codigo;
                                        if (codigo == null) return;
                                        const PAGE_SIZE = 30;
                                        (async () => {
                                            try {
                                                setTarjetaLoading(true);
                                                const items = await tesoreriaService.getMediosTarjetaPorGrupoPaged(codigo, { pageNumber: tarjetaPage, pageSize: PAGE_SIZE });
                                                // evitar duplicados por id
                                                const existing = new Set(mediosTarjeta.map(m => String(m.id)));
                                                const nuevos = (items || []).filter(m => !existing.has(String(m.id)));
                                                setTarjetaHasMore(nuevos.length > 0);
                                                if (nuevos.length > 0) {
                                                    setTarjetaPage(prev => prev + 1);
                                                    setMediosTarjeta(prev => [...prev, ...nuevos]);
                                                }
                                            } finally {
                                                setTarjetaLoading(false);
                                            }
                                        })();
                                    }
                                }}
                            >
                                <RadioGroup
                                    name="tarjetaOption"
                                    value={tarjetaSeleccionadaIdx ?? ''}
                                    onChange={(e) => {
                                        const newIdx = String(e.target.value);
                                        setTarjetaSeleccionadaIdx(newIdx);
                                        const idx = Number(newIdx);
                                        if (!isNaN(idx) && idx >= 0 && idx < mediosTarjeta.length) {
                                            const medioSel = mediosTarjeta[idx];
                                            const idNum = Number(medioSel.id);
                                            setPagoActual(prev => ({
                                                ...prev,
                                                nroCuenta: Number.isFinite(idNum) ? idNum : undefined,
                                                medioLabel: medioSel.nombre,
                                                mcp: medioSel.mcp || medioSel.nombre,
                                            }));
                                        }
                                    }}
                                >
                                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                        {mediosTarjeta.map((medio, idx) => (
                                            <Grid item xs={12} key={medio.id ?? idx}>
                                                <Card variant={(String(tarjetaSeleccionadaIdx) === String(idx)) ? 'elevation' : 'outlined'}>
                                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <FormControlLabel
                                                            value={String(idx)}
                                                            control={<Radio />}
                                                            label={
                                                                <Box>
                                                                    <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                                    {medio.descripcion && (
                                                                        <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                                    )}
                                                                    {medio.clase && (
                                                                        <Typography variant="caption">{medio.clase}</Typography>
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{ m: 0 }}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                        {mediosTarjeta.length === 0 && !tarjetaLoading && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">No hay medios de tarjeta disponibles para este grupo.</Typography>
                                            </Grid>
                                        )}
                                        {tarjetaLoading && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">Cargando más medios...</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </RadioGroup>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <CurrencyTextField
                                fullWidth
                                label="Monto"
                                value={pagoActual.importe ?? 0}
                                onChange={(v) => setPagoActual((prev) => ({ ...prev, importe: v }))}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Observaciones (opcional)"
                                name="detalle"
                                value={pagoActual.detalle}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>
                    </Grid>
                );

            case 'Cheque':
                return (
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                CHEQUES DE TERCERO - Seleccione el medio específico:
                            </Typography>
                            <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                <RadioGroup
                                    name="chequeOption"
                                    value={chequeSeleccionadoIdx ?? ''}
                                    onChange={(e) => {
                                        const newIdx = String(e.target.value);
                                        setChequeSeleccionadoIdx(newIdx);
                                        const idx = Number(newIdx);
                                        if (!isNaN(idx) && idx >= 0 && idx < mediosCheques.length) {
                                            const medioSel = mediosCheques[idx];
                                            setPagoActual(prev => ({ ...prev, medioLabel: medioSel.nombre, mcp: medioSel.mcp || medioSel.nombre }));
                                        }
                                    }}
                                >
                                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                        {mediosCheques.map((medio, idx) => (
                                            <Grid item xs={12} key={medio.id ?? idx}>
                                                <Card variant={(String(chequeSeleccionadoIdx) === String(idx)) ? 'elevation' : 'outlined'}>
                                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <FormControlLabel
                                                            value={String(idx)}
                                                            control={<Radio />}
                                                            label={
                                                                <Box>
                                                                    <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                                    {medio.descripcion && (
                                                                        <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                                    )}
                                                                    {medio.clase && (
                                                                        <Typography variant="caption">{medio.clase}</Typography>
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{ m: 0 }}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                        {mediosCheques.length === 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">No hay medios de cheques disponibles para este grupo.</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </RadioGroup>
                            </Box>
                        </Grid>

                        {esChequeCartera ? (
                            <>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                            Seleccione uno o varios cheques de cartera:
                                        </Typography>
                                        {isTabletOrLarger && chequesCartera.length > 0 && (() => {
                                            const terminoBusqueda = busquedaCheque.toLowerCase().trim();
                                            const chequesFiltrados = terminoBusqueda
                                                ? chequesCartera.filter(cheque => {
                                                    const coincideTexto = 
                                                        cheque.nombreBanco?.toLowerCase().includes(terminoBusqueda) ||
                                                        String(cheque.nroCheque).includes(terminoBusqueda) ||
                                                        cheque.librador?.toLowerCase().includes(terminoBusqueda) ||
                                                        cheque.nombreSucursal?.toLowerCase().includes(terminoBusqueda) ||
                                                        cheque.concepto?.toLowerCase().includes(terminoBusqueda) ||
                                                        String(cheque.nroSucursal).includes(terminoBusqueda);
                                                    const montoBusqueda = parseFloat(terminoBusqueda.replace(/[^\d.,]/g, '').replace(',', '.'));
                                                    const coincideMonto = !isNaN(montoBusqueda) && 
                                                        Math.abs(cheque.importe - montoBusqueda) < 0.01;
                                                    return coincideTexto || coincideMonto;
                                                })
                                                : chequesCartera;
                                            const chequesOrdenados = ordenarCheques(chequesFiltrados);
                                            const todosSeleccionados = chequesOrdenados.length > 0 && 
                                                chequesOrdenados.every(cheque => chequesCarteraSeleccionados.has(cheque.nroCarga));
                                            return (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => handleSeleccionarTodos(chequesOrdenados)}
                                                    sx={{ minWidth: '140px' }}
                                                >
                                                    {todosSeleccionados ? 'Deseleccionar todos' : 'Seleccionar todos'}
                                                </Button>
                                            );
                                        })()}
                                    </Box>
                                    <TextField
                                        fullWidth
                                        placeholder="Buscar por banco, número de cheque, librador, monto..."
                                        value={busquedaCheque}
                                        onChange={(e) => setBusquedaCheque(e.target.value)}
                                        size="small"
                                        sx={{ mb: 2 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    {loadingChequesCartera ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                            <Typography>Cargando cheques de cartera...</Typography>
                                        </Box>
                                    ) : chequesCartera.length === 0 ? (
                                        <Box sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No hay cheques disponibles en cartera.
                                            </Typography>
                                        </Box>
                                    ) : (() => {
                                        // Filtrar cheques basado en la búsqueda
                                        const terminoBusqueda = busquedaCheque.toLowerCase().trim();
                                        const chequesFiltrados = terminoBusqueda
                                            ? chequesCartera.filter(cheque => {
                                                // Buscar por texto (banco, librador, etc.)
                                                const coincideTexto = 
                                                    cheque.nombreBanco?.toLowerCase().includes(terminoBusqueda) ||
                                                    String(cheque.nroCheque).includes(terminoBusqueda) ||
                                                    cheque.librador?.toLowerCase().includes(terminoBusqueda) ||
                                                    cheque.nombreSucursal?.toLowerCase().includes(terminoBusqueda) ||
                                                    cheque.concepto?.toLowerCase().includes(terminoBusqueda) ||
                                                    String(cheque.nroSucursal).includes(terminoBusqueda);
                                                
                                                // Buscar por monto (acepta números con o sin separadores)
                                                const montoBusqueda = parseFloat(terminoBusqueda.replace(/[^\d.,]/g, '').replace(',', '.'));
                                                const coincideMonto = !isNaN(montoBusqueda) && 
                                                    Math.abs(cheque.importe - montoBusqueda) < 0.01;
                                                
                                                return coincideTexto || coincideMonto;
                                            })
                                            : chequesCartera;

                                        // Ordenar cheques filtrados
                                        const chequesOrdenados = ordenarCheques(chequesFiltrados);
                                        const todosSeleccionados = chequesOrdenados.length > 0 && 
                                            chequesOrdenados.every(cheque => chequesCarteraSeleccionados.has(cheque.nroCarga));

                                        if (isTabletOrLarger) {
                                            return (
                                            // Vista de tabla para notebooks/pantallas grandes
                                            <TableContainer component={Paper} sx={{ maxHeight: { xs: '50vh', sm: 400 }, overflowY: 'auto' }}>
                                                <Table size="small" stickyHeader>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell padding="checkbox" sx={{ width: 50 }}>
                                                                <Checkbox
                                                                    indeterminate={chequesCarteraSeleccionados.size > 0 && !todosSeleccionados}
                                                                    checked={todosSeleccionados && chequesOrdenados.length > 0}
                                                                    onChange={() => handleSeleccionarTodos(chequesOrdenados)}
                                                                    inputProps={{ 'aria-label': 'seleccionar todos los cheques' }}
                                                                />
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>
                                                                <TableSortLabel
                                                                    active={orderBy === 'nombreBanco'}
                                                                    direction={orderBy === 'nombreBanco' ? order : 'asc'}
                                                                    onClick={() => handleRequestSort('nombreBanco')}
                                                                >
                                                                    Banco
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>
                                                                <TableSortLabel
                                                                    active={orderBy === 'nroCheque'}
                                                                    direction={orderBy === 'nroCheque' ? order : 'asc'}
                                                                    onClick={() => handleRequestSort('nroCheque')}
                                                                >
                                                                    N° Cheque
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>
                                                                <TableSortLabel
                                                                    active={orderBy === 'nombreSucursal'}
                                                                    direction={orderBy === 'nombreSucursal' ? order : 'asc'}
                                                                    onClick={() => handleRequestSort('nombreSucursal')}
                                                                >
                                                                    Sucursal
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                                <TableSortLabel
                                                                    active={orderBy === 'importe'}
                                                                    direction={orderBy === 'importe' ? order : 'asc'}
                                                                    onClick={() => handleRequestSort('importe')}
                                                                >
                                                                    Monto
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>
                                                                <TableSortLabel
                                                                    active={orderBy === 'librador'}
                                                                    direction={orderBy === 'librador' ? order : 'asc'}
                                                                    onClick={() => handleRequestSort('librador')}
                                                                >
                                                                    Librador
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>
                                                                <TableSortLabel
                                                                    active={orderBy === 'fechaEmision'}
                                                                    direction={orderBy === 'fechaEmision' ? order : 'asc'}
                                                                    onClick={() => handleRequestSort('fechaEmision')}
                                                                >
                                                                    Emisión
                                                                </TableSortLabel>
                                                            </TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>
                                                                <TableSortLabel
                                                                    active={orderBy === 'fechaVencimiento'}
                                                                    direction={orderBy === 'fechaVencimiento' ? order : 'asc'}
                                                                    onClick={() => handleRequestSort('fechaVencimiento')}
                                                                >
                                                                    Vencimiento
                                                                </TableSortLabel>
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {chequesOrdenados.length === 0 ? (
                                                            <TableRow>
                                                                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        No se encontraron cheques que coincidan con la búsqueda
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        ) : (
                                                            chequesOrdenados.map((cheque) => {
                                                        const estaSeleccionado = chequesCarteraSeleccionados.has(cheque.nroCarga);
                                                        return (
                                                            <TableRow
                                                                key={cheque.nroCarga}
                                                                hover
                                                                selected={estaSeleccionado}
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    bgcolor: estaSeleccionado ? 'action.selected' : 'inherit',
                                                                    '&:hover': {
                                                                        bgcolor: estaSeleccionado ? 'action.selected' : 'action.hover',
                                                                    }
                                                                }}
                                                                onClick={() => {
                                                                    const nuevoSet = new Set(chequesCarteraSeleccionados);
                                                                    if (estaSeleccionado) {
                                                                        nuevoSet.delete(cheque.nroCarga);
                                                                    } else {
                                                                        nuevoSet.add(cheque.nroCarga);
                                                                    }
                                                                    setChequesCarteraSeleccionados(nuevoSet);
                                                                }}
                                                            >
                                                                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                                                    <Checkbox
                                                                        checked={estaSeleccionado}
                                                                        onChange={(e) => {
                                                                            const nuevoSet = new Set(chequesCarteraSeleccionados);
                                                                            if (e.target.checked) {
                                                                                nuevoSet.add(cheque.nroCarga);
                                                                            } else {
                                                                                nuevoSet.delete(cheque.nroCarga);
                                                                            }
                                                                            setChequesCarteraSeleccionados(nuevoSet);
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>{cheque.nombreBanco}</TableCell>
                                                                <TableCell>{cheque.nroCheque}</TableCell>
                                                                <TableCell>{cheque.nombreSucursal} ({cheque.nroSucursal})</TableCell>
                                                                <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                                                    {formatCurrency(cheque.importe)}
                                                                </TableCell>
                                                                <TableCell>{cheque.librador}</TableCell>
                                                                <TableCell>
                                                                    {cheque.fechaEmision ? new Date(cheque.fechaEmision).toLocaleDateString() : '-'}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {cheque.fechaVencimiento ? new Date(cheque.fechaVencimiento).toLocaleDateString() : '-'}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                            })
                                                        )}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                            );
                                        } else {
                                            return (
                                                // Vista de tarjetas para móviles/pantallas pequeñas
                                                <Box sx={{ maxHeight: { xs: '50vh', sm: 400 }, overflowY: 'auto', pr: 1 }}>
                                                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                                        {chequesFiltrados.length === 0 ? (
                                                            <Grid item xs={12}>
                                                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        No se encontraron cheques que coincidan con la búsqueda
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                        ) : (
                                                            chequesOrdenados.map((cheque) => {
                                                    const estaSeleccionado = chequesCarteraSeleccionados.has(cheque.nroCarga);
                                                    return (
                                                        <Grid item xs={12} key={cheque.nroCarga}>
                                                            <Card variant={estaSeleccionado ? 'elevation' : 'outlined'} sx={{ borderColor: estaSeleccionado ? 'primary.main' : undefined }}>
                                                                <CardContent>
                                                                    <FormControlLabel
                                                                        control={
                                                                            <Checkbox
                                                                                checked={estaSeleccionado}
                                                                                onChange={(e) => {
                                                                                    const nuevoSet = new Set(chequesCarteraSeleccionados);
                                                                                    if (e.target.checked) {
                                                                                        nuevoSet.add(cheque.nroCarga);
                                                                                    } else {
                                                                                        nuevoSet.delete(cheque.nroCarga);
                                                                                    }
                                                                                    setChequesCarteraSeleccionados(nuevoSet);
                                                                                }}
                                                                            />
                                                                        }
                                                                        label={
                                                                            <Box sx={{ width: '100%' }}>
                                                                                <Grid container spacing={1}>
                                                                                    <Grid item xs={12} sm={6}>
                                                                                        <Typography sx={{ fontWeight: 600 }}>
                                                                                            Banco: {cheque.nombreBanco}
                                                                                        </Typography>
                                                                                    </Grid>
                                                                                    <Grid item xs={12} sm={6}>
                                                                                        <Typography variant="body2">
                                                                                            Cheque N°: {cheque.nroCheque}
                                                                                        </Typography>
                                                                                    </Grid>
                                                                                    <Grid item xs={12} sm={6}>
                                                                                        <Typography variant="body2">
                                                                                            Sucursal: {cheque.nombreSucursal} ({cheque.nroSucursal})
                                                                                        </Typography>
                                                                                    </Grid>
                                                                                    <Grid item xs={12} sm={6}>
                                                                                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                                                                            Monto: {formatCurrency(cheque.importe)}
                                                                                        </Typography>
                                                                                    </Grid>
                                                                                    <Grid item xs={12} sm={6}>
                                                                                        <Typography variant="body2">
                                                                                            Librador: {cheque.librador}
                                                                                        </Typography>
                                                                                    </Grid>
                                                                                    <Grid item xs={12} sm={6}>
                                                                                        <Typography variant="body2">
                                                                                            Emisión: {cheque.fechaEmision ? new Date(cheque.fechaEmision).toLocaleDateString() : '-'}
                                                                                        </Typography>
                                                                                    </Grid>
                                                                                    <Grid item xs={12} sm={6}>
                                                                                        <Typography variant="body2">
                                                                                            Vencimiento: {cheque.fechaVencimiento ? new Date(cheque.fechaVencimiento).toLocaleDateString() : '-'}
                                                                                        </Typography>
                                                                                    </Grid>
                                                                                    {cheque.concepto && (
                                                                                        <Grid item xs={12}>
                                                                                            <Typography variant="caption" color="text.secondary">
                                                                                                Concepto: {cheque.concepto}
                                                                                            </Typography>
                                                                                        </Grid>
                                                                                    )}
                                                                                </Grid>
                                                                            </Box>
                                                                        }
                                                                        sx={{ m: 0, width: '100%' }}
                                                                    />
                                                                </CardContent>
                                                            </Card>
                                                        </Grid>
                                                            );
                                                        })
                                                        )}
                                                    </Grid>
                                                </Box>
                                            );
                                        }
                                    })()}
                                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                        {chequesCarteraSeleccionados.size > 0 && (
                                            <Box sx={{ p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider', flex: 1, minWidth: '200px' }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                    {chequesCarteraSeleccionados.size} cheque{chequesCarteraSeleccionados.size > 1 ? 's' : ''} seleccionado{chequesCarteraSeleccionados.size > 1 ? 's' : ''}
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                                    Total: {formatCurrency(totalImporteSeleccionado)}
                                                </Typography>
                                            </Box>
                                        )}
                                        {!isTabletOrLarger && chequesCartera.length > 0 && (() => {
                                            const terminoBusqueda = busquedaCheque.toLowerCase().trim();
                                            const chequesFiltrados = terminoBusqueda
                                                ? chequesCartera.filter(cheque => {
                                                    const coincideTexto = 
                                                        cheque.nombreBanco?.toLowerCase().includes(terminoBusqueda) ||
                                                        String(cheque.nroCheque).includes(terminoBusqueda) ||
                                                        cheque.librador?.toLowerCase().includes(terminoBusqueda) ||
                                                        cheque.nombreSucursal?.toLowerCase().includes(terminoBusqueda) ||
                                                        cheque.concepto?.toLowerCase().includes(terminoBusqueda) ||
                                                        String(cheque.nroSucursal).includes(terminoBusqueda);
                                                    const montoBusqueda = parseFloat(terminoBusqueda.replace(/[^\d.,]/g, '').replace(',', '.'));
                                                    const coincideMonto = !isNaN(montoBusqueda) && 
                                                        Math.abs(cheque.importe - montoBusqueda) < 0.01;
                                                    return coincideTexto || coincideMonto;
                                                })
                                                : chequesCartera;
                                            const chequesOrdenados = ordenarCheques(chequesFiltrados);
                                            const todosSeleccionados = chequesOrdenados.length > 0 && 
                                                chequesOrdenados.every(cheque => chequesCarteraSeleccionados.has(cheque.nroCarga));
                                            return (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => handleSeleccionarTodos(chequesOrdenados)}
                                                    sx={{ minWidth: '140px' }}
                                                >
                                                    {todosSeleccionados ? 'Deseleccionar todos' : 'Seleccionar todos'}
                                                </Button>
                                            );
                                        })()}
                                    </Box>
                                </Grid>
                            </>
                        ) : (
                            <>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="* Banco"
                                        value={pagoActual.banco || ''}
                                        placeholder="Seleccionar banco..."
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setOpenBancoDialog(true)}>
                                                        <ArrowDropDownIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        onClick={() => setOpenBancoDialog(true)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="N° Cheque"
                                        name="nroCheque"
                                        value={pagoActual.nroCheque || ''}
                                        onChange={handlePagoFieldChange}
                                        required
                                        size={isSmallScreen ? "small" : "medium"}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="N° Sucursal"
                                        name="nroSucursal"
                                        type="number"
                                        value={pagoActual.nroSucursal ?? ''}
                                        onChange={handlePagoFieldChange}
                                        required
                                        size={isSmallScreen ? "small" : "medium"}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <CurrencyTextField
                                        fullWidth
                                        label="Monto"
                                        value={pagoActual.importe ?? 0}
                                        onChange={(v) => setPagoActual((prev) => ({ ...prev, importe: v }))}
                                        required
                                        size={isSmallScreen ? 'small' : 'medium'}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Fecha de Emisión"
                                        name="fechaEmision"
                                        type="date"
                                        value={pagoActual.fechaEmision}
                                        onChange={handlePagoFieldChange}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                        size={isSmallScreen ? "small" : "medium"}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Fecha de Vencimiento"
                                        name="fechaVencimiento"
                                        type="date"
                                        value={pagoActual.fechaVencimiento}
                                        onChange={handlePagoFieldChange}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                        size={isSmallScreen ? "small" : "medium"}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Cta. Bancaria"
                                        name="ctaBancaria"
                                        value={pagoActual.ctaBancaria || ''}
                                        onChange={handlePagoFieldChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Librador"
                                        name="librador"
                                        value={((pagoActual.librador || formulario.nombre) || '').slice(0, 25)}
                                        onChange={handlePagoFieldChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Cuit Librador"
                                        name="nroLibrador"
                                        value={pagoActual.nroLibrador || ''}
                                        onChange={handlePagoFieldChange}
                                        required
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Concepto"
                                        name="concepto"
                                        value={pagoActual.concepto || ''}
                                        onChange={handlePagoFieldChange}
                                        multiline
                                        rows={2}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                );

            case 'ChequePropio':
                return (
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                CHEQUE PROPIO - Seleccione la cartera / medio:
                            </Typography>
                            <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                <RadioGroup
                                    name="chequePropioOption"
                                    value={chequeSeleccionadoIdx ?? ''}
                                    onChange={(e) => {
                                        const newIdx = String(e.target.value);
                                        setChequeSeleccionadoIdx(newIdx);
                                        const idx = Number(newIdx);
                                        if (!isNaN(idx) && idx >= 0 && idx < mediosCheques.length) {
                                            const medioSel = mediosCheques[idx];
                                            setPagoActual(prev => ({
                                                ...prev,
                                                medioLabel: medioSel.nombre,
                                                mcp: medioSel.mcp || medioSel.nombre,
                                                // Limpiar hasta resolver cartera / códigos del cheque
                                                banco: undefined,
                                                cartera: undefined,
                                                nroBanco: undefined,
                                                nroSucursal: undefined,
                                                ctaBancaria: undefined,
                                            }));
                                            setAdvertenciasCarteraChequePropio([]);
                                            setCargandoCarteraChequePropio(true);
                                            void bancoDesdeCarteraMcp(medioSel).then((bancoInfo) => {
                                                setAdvertenciasCarteraChequePropio(bancoInfo.advertencias);
                                                setPagoActual((prev) => ({
                                                    ...prev,
                                                    banco: bancoInfo.banco,
                                                    cartera: bancoInfo.cartera > 0 ? bancoInfo.cartera : undefined,
                                                    nroBanco: bancoInfo.nroBanco,
                                                    nroSucursal: bancoInfo.nroSucursal,
                                                    ...(bancoInfo.ctaBancaria
                                                        ? { ctaBancaria: bancoInfo.ctaBancaria }
                                                        : {}),
                                                }));
                                            }).finally(() => {
                                                setCargandoCarteraChequePropio(false);
                                            });
                                        }
                                    }}
                                >
                                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                        {mediosCheques.map((medio, idx) => (
                                            <Grid item xs={12} key={medio.id ?? idx}>
                                                <Card variant={(String(chequeSeleccionadoIdx) === String(idx)) ? 'elevation' : 'outlined'}>
                                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <FormControlLabel
                                                            value={String(idx)}
                                                            control={<Radio />}
                                                            label={
                                                                <Box>
                                                                    <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                                    {medio.descripcion && (
                                                                        <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                                    )}
                                                                    {medio.clase && (
                                                                        <Typography variant="caption">{medio.clase}</Typography>
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{ m: 0 }}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                        {mediosCheques.length === 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">No hay medios de cheques propios disponibles para este grupo.</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </RadioGroup>
                            </Box>
                        </Grid>

                        {(cargandoCarteraChequePropio || chequeSeleccionadoIdx != null) && (
                            <Grid item xs={12}>
                                {cargandoCarteraChequePropio ? (
                                    <Alert severity="info" icon={<CircularProgress size={18} />}>
                                        Verificando datos de la cartera…
                                    </Alert>
                                ) : (
                                    <>
                                        {(pagoActual.banco ||
                                            pagoActual.nroBanco != null ||
                                            pagoActual.ctaBancaria ||
                                            pagoActual.cartera != null) && (
                                            <Box sx={{ mb: advertenciasCarteraChequePropio.length > 0 ? 1.5 : 0 }}>
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                    Datos resueltos desde la cartera
                                                    {pagoActual.cartera != null ? ` N° ${pagoActual.cartera}` : ''}:
                                                </Typography>
                                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                                    {pagoActual.banco && (
                                                        <Chip size="small" label={`Banco: ${pagoActual.banco}`} variant="outlined" />
                                                    )}
                                                    {pagoActual.nroBanco != null && (
                                                        <Chip size="small" label={`N° Banco: ${pagoActual.nroBanco}`} variant="outlined" />
                                                    )}
                                                    {pagoActual.nroSucursal != null && (
                                                        <Chip size="small" label={`N° Sucursal: ${pagoActual.nroSucursal}`} variant="outlined" />
                                                    )}
                                                    {pagoActual.ctaBancaria && (
                                                        <Chip size="small" label={`Cuenta: ${pagoActual.ctaBancaria}`} variant="outlined" />
                                                    )}
                                                </Stack>
                                            </Box>
                                        )}
                                        {advertenciasCarteraChequePropio.length > 0 && (
                                            <Alert severity="warning" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                                    Falta CBU en la cartera
                                                </Typography>
                                                <List dense disablePadding>
                                                    {advertenciasCarteraChequePropio.map((msg) => (
                                                        <ListItem key={msg} disableGutters sx={{ py: 0.25, alignItems: 'flex-start' }}>
                                                            <ListItemText
                                                                primary={`• ${msg}`}
                                                                primaryTypographyProps={{ variant: 'body2' }}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Alert>
                                        )}
                                    </>
                                )}
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                Datos del cheque emitido
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Fecha de Emisión"
                                name="fechaEmision"
                                type="date"
                                value={pagoActual.fechaEmision}
                                onChange={handlePagoFieldChange}
                                InputLabelProps={{ shrink: true }}
                                required
                                size={isSmallScreen ? 'small' : 'medium'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="F. Vencimiento"
                                name="fechaVencimiento"
                                type="date"
                                value={pagoActual.fechaVencimiento}
                                onChange={handlePagoFieldChange}
                                InputLabelProps={{ shrink: true }}
                                required
                                size={isSmallScreen ? 'small' : 'medium'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Nº Cheque"
                                name="nroCheque"
                                value={pagoActual.nroCheque || ''}
                                onChange={handlePagoFieldChange}
                                required
                                size={isSmallScreen ? 'small' : 'medium'}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <CurrencyTextField
                                fullWidth
                                label="Importe"
                                value={pagoActual.importe ?? 0}
                                onChange={(v) => setPagoActual((prev) => ({ ...prev, importe: v }))}
                                required
                                size={isSmallScreen ? 'small' : 'medium'}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <SearchableSelect
                                label="Compañía (librador)"
                                value={companiaLibradorId}
                                onChange={(v) => {
                                    const id = v ?? null;
                                    setCompaniaLibradorId(id);
                                    const compania = companiasLibrador.find((c) => c.idCompania === id);
                                    if (compania) {
                                        const datos = libradorDesdeCompania(compania);
                                        setPagoActual((prev) => ({ ...prev, ...datos }));
                                    } else {
                                        setPagoActual((prev) => ({
                                            ...prev,
                                            librador: '',
                                            nroLibrador: '',
                                        }));
                                    }
                                }}
                                options={companiasLibrador.map((c) => ({
                                    value: c.idCompania,
                                    label: `${c.nombre}${c.cuit ? ` · ${c.cuit}` : ''}`,
                                }))}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Concepto"
                                name="concepto"
                                value={pagoActual.concepto || ''}
                                onChange={handlePagoFieldChange}
                                multiline
                                rows={2}
                            />
                        </Grid>
                    </Grid>
                );

            case 'Otros':
                return (
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                OTROS - Seleccione el medio específico:
                            </Typography>
                            <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                                <RadioGroup
                                    name="otrosOption"
                                    value={otrosSeleccionadoIdx ?? ''}
                                    onChange={(e) => {
                                        const newIdx = String(e.target.value);
                                        setOtrosSeleccionadoIdx(newIdx);
                                        const idx = Number(newIdx);
                                        if (!isNaN(idx) && idx >= 0 && idx < mediosOtros.length) {
                                            const medioSel = mediosOtros[idx];
                                            const idNum = Number(medioSel.id);
                                            setPagoActual(prev => ({ ...prev, nroCuenta: Number.isFinite(idNum) ? idNum : undefined, medioLabel: medioSel.nombre, mcp: medioSel.mcp || medioSel.nombre }));
                                        }
                                    }}
                                >
                                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                        {mediosOtros.map((medio, idx) => (
                                            <Grid item xs={12} key={medio.id ?? idx}>
                                                <Card variant={(String(otrosSeleccionadoIdx) === String(idx)) ? 'elevation' : 'outlined'}>
                                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <FormControlLabel
                                                            value={String(idx)}
                                                            control={<Radio />}
                                                            label={
                                                                <Box>
                                                                    <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                                                    {medio.descripcion && (
                                                                        <Typography variant="caption" display="block">{medio.descripcion}</Typography>
                                                                    )}
                                                                    {medio.clase && (
                                                                        <Typography variant="caption">{medio.clase}</Typography>
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{ m: 0 }}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        ))}
                                        {mediosOtros.length === 0 && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2">No hay medios disponibles para este grupo.</Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </RadioGroup>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <CurrencyTextField
                                fullWidth
                                label="Monto"
                                value={pagoActual.importe ?? 0}
                                onChange={(v) => setPagoActual((prev) => ({ ...prev, importe: v }))}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Observaciones (opcional)"
                                name="detalle"
                                value={pagoActual.detalle}
                                onChange={handlePagoFieldChange}
                            />
                        </Grid>
                    </Grid>
                );

            default:
                return null;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const detallesValidos = detallesPago
                .filter((pago) => importeMedioOrdenPagoValidoParaEnvio(pago))
                .map(({ medioLabel, ...rest }) => rest);
            if (detallesValidos.length === 0) {
                enqueueSnackbar(
                    'Debe agregar al menos un medio de pago con monto distinto de cero (en efectivo puede usar monto negativo para vuelto).',
                    { variant: 'warning' }
                );
                return;
            }

            if (comprobantesSeleccionados.size > 0 && diferenciaInsuficiente) {
                enqueueSnackbar(
                    `Monto insuficiente. Agregá otro medio de pago por ${formatCurrency(Math.abs(diferencia))}.`,
                    { variant: 'error' }
                );
                return;
            }

            const total = detallesValidos.reduce((sum, p) => sum + (p.importe || 0), 0);
            if (total <= 0) {
                enqueueSnackbar('El monto total debe ser mayor a 0', { variant: 'warning' });
                return;
            }

            // Determinar grupo MCP SOLO desde el Grupo de Caja seleccionado
            const storedGrupo = Number(localStorage.getItem('grupoCajaMcp') || 0);
            const grupoMCP = (grupoCajaSeleccionado?.codigo ?? (Number.isFinite(storedGrupo) && storedGrupo > 0 ? storedGrupo : undefined)) as number | undefined;
            if (!grupoMCP || !Number.isFinite(grupoMCP)) {
                enqueueSnackbar('Debe seleccionar un Grupo de Caja válido antes de continuar', { variant: 'warning' });
                setOpenGrupoCajaDialog(true);
                return;
            }

            // Validaciones adicionales de negocio y tipos antes de construir el payload
            const errores: string[] = [];
            // Encabezado
            if (!Number.isFinite(Number(formulario.nroProveedor)) || Number(formulario.nroProveedor) <= 0) {
                errores.push('Número de Proveedor es obligatorio y debe ser numérico (>0)');
            }
            const ns = Number(formulario.nroSuc);
            if (!Number.isFinite(ns) || ns <= 0) {
                errores.push('Nº Sucursal es obligatorio y debe ser numérico (>0)');
            }

            // Detalles de pago
            detallesValidos.forEach((p, idx) => {
                const idxLabel = `Pago ${idx + 1}`;
                if (!p.mcp || String(p.mcp).trim() === '') errores.push(`${idxLabel}: Debe seleccionar el medio específico (MCP)`);
                const impP = Number(p.importe);
                const esEf = (p.tipoPago || '').toLowerCase() === 'efectivo';
                if (!Number.isFinite(impP) || impP === 0) errores.push(`${idxLabel}: Monto debe ser distinto de cero`);
                else if (!esEf && impP <= 0) errores.push(`${idxLabel}: Monto debe ser mayor a cero (solo efectivo admite vuelto negativo)`);

                const tipo = (p.tipoPago || '').toLowerCase();
                if (tipo === 'cheque' || tipo === 'chequepropio') {
                    const nroBanco = Number(p.nroBanco);
                    const nroSucursal = Number(p.nroSucursal);
                    if (tipo === 'chequepropio') {
                        if (!Number.isFinite(nroBanco) || nroBanco < 1 || nroBanco > 999) {
                            errores.push(
                                `${idxLabel}: No se pudo obtener el N° Banco (1–999) desde la cartera. Verificá que la cartera tenga CBU cargado.`
                            );
                        }
                        if (!Number.isFinite(nroSucursal) || nroSucursal < 1 || nroSucursal > 999) {
                            errores.push(
                                `${idxLabel}: No se pudo obtener el N° Sucursal (1–999) desde la cartera. Verificá el CBU de la cartera.`
                            );
                        }
                    } else {
                        if (!Number.isFinite(nroBanco)) errores.push(`${idxLabel}: Banco inválido`);
                        if (!Number.isFinite(nroSucursal)) errores.push(`${idxLabel}: Nº Sucursal inválido`);
                    }
                    if (!p.nroCheque || String(p.nroCheque).trim() === '') errores.push(`${idxLabel}: Nº Cheque es obligatorio`);
                    const libradorEfectivo = String(p.librador || p.nroLibrador || '').trim();
                    if (!libradorEfectivo) errores.push(`${idxLabel}: Librador es obligatorio`);
                    if (tipo === 'chequepropio' && !String(p.nroLibrador || '').trim()) {
                        errores.push(`${idxLabel}: Nº Librador (CUIT de la compañía) es obligatorio`);
                    }
                    // Fechas
                    if (!p.fechaEmision || isNaN(new Date(p.fechaEmision as any).getTime())) errores.push(`${idxLabel}: Fecha de Emisión inválida`);
                    if (!p.fechaVencimiento || isNaN(new Date(p.fechaVencimiento as any).getTime())) errores.push(`${idxLabel}: Fecha de Vencimiento inválida`);
                }
                if (tipo === 'tarjeta') {
                    // Validaciones eliminadas - solo se requiere medio y monto
                }
            });

            if (errores.length > 0) {
                enqueueSnackbar(errores.join(' | '), { variant: 'error' });
                return;
            }

            // Construir mediosDeCobro según el endpoint requerido
            const mediosDeCobro = detallesValidos.map((p) => {
                const tipoUI = (p.tipoPago || '').toLowerCase();
                const esCheque = tipoUI === 'cheque' || tipoUI === 'chequepropio';
                const tipoPagoApi =
                    tipoUI === 'chequepropio' ? 'P' : (p.tipoPago || '');
                const base: any = {
                    mcp: p.mcp || '',
                    importe: p.importe,
                    // "detalle" siempre sale de Observaciones / Destino del formulario
                    detalle: (p.detalle || ''),
                    // Cheque propio: código "P"; resto mantiene el nombre de UI (compatibilidad actual)
                    tipoPago: tipoPagoApi
                };
                if (esCheque) {
                    base.cheque = {
                        nroBanco: p.nroBanco ?? 0,
                        nroSucursal: p.nroSucursal ?? 0,
                        nroCheque: p.nroCheque || '',
                        nroLibrador: p.nroLibrador || '',
                        librador: (p.librador || p.nroLibrador || '').trim().slice(0, 25),
                        fechaEmision: p.fechaEmision ? new Date(p.fechaEmision).toISOString() : new Date().toISOString(),
                        fechaVencimiento: p.fechaVencimiento ? new Date(p.fechaVencimiento).toISOString() : new Date().toISOString(),
                        ctaBancaria: p.ctaBancaria || '',
                        concepto: p.concepto || '',
                        ...(p.nroCarga ? { nroCarga: p.nroCarga } : {})
                    };
                }
                if (tipoUI === 'chequepropio' && p.cartera != null && Number(p.cartera) > 0) {
                    base.cartera = Number(p.cartera);
                }
                if ((p.tipoPago || '').toLowerCase() === 'tarjeta') {
                    base.tarjeta = {
                        codigoMarca: 0,
                        nroTarjeta: '',
                        nroComprobanteTa: 0,
                        fecha: new Date().toISOString(),
                        fechaPresentar: new Date().toISOString(),
                        numeroroAutorizacion: '',
                        telefono: '',
                        titularTarjeta: ''
                    };
                }
                return base;
            });

            // Construir documentosAplicar: distribuir Total Medios entre comprobantes seleccionados
            let documentosAplicar: { nroIdformulario: number; montoCancelado: number }[] = [];
            if (mediosDeCobro.length > 0 && total > 0 && comprobantesSeleccionados.size > 0) {
                // Iterar en el orden actual de los comprobantes y aplicar a los seleccionados
                let restante = total;
                for (const comp of comprobantesDeuda) {
                    if (restante <= 0) break;
                    if (!comprobantesSeleccionados.has(comp.id)) continue;
                    if (!esComprobanteImputableOrdenPago(comp)) continue;
                    if (!comp.nroFormulario) continue; // Solo documentos aplicables

                    const saldoDisponible = montoImputableComprobante(comp);
                    if (saldoDisponible <= 0) continue;

                    // Si el usuario ingresó un monto específico en "Aplicar", respetarlo hasta saldo/restante
                    const montoDeseado = montosAplicar.get(comp.id);
                    let aAplicar = Number.isFinite(Number(montoDeseado)) ? Number(montoDeseado) : saldoDisponible;
                    aAplicar = Math.max(0, Math.min(aAplicar, saldoDisponible, restante));

                    if (aAplicar > 0) {
                        documentosAplicar.push({ nroIdformulario: comp.nroFormulario, montoCancelado: aAplicar });
                        restante -= aAplicar;
                    }
                }
                // Si no se pudo aplicar nada (p. ej. sin nroFormulario o saldos 0), dejamos documentosAplicar vacío
            }

            console.log('📋 Documentos a aplicar a enviar:', documentosAplicar);

            // Tomar sucursal desde el formulario (si no viene, usar 0)
            const nroSucValue: number = Number(formulario.nroSuc) || 0;
            const payload = {
                nroProveedor: formulario.nroProveedor,
                nroSuc: nroSucValue,
                grupoMCP,
                // Cabecera: respetar la fecha elegida por el usuario
                fechaEmision: formulario.fechaEmision,
                descripcion: formulario.descripcion || '',
                mediosDePago: mediosDeCobro,
                documentosAplicar: documentosAplicar.length > 0 ? documentosAplicar : undefined
            };

            console.log('📤 Payload completo a enviar:', payload);

            try {
                const response = await api.post('/api/Tesoreria/ordendepago', payload);
                console.log('✅ Respuesta del servidor:', response.data);
                
                const ordenPagoCreada = response.data;
                
                // Agregar la orden de pago creada a la lista de comprobantes de deuda
                if (ordenPagoCreada && ordenPagoCreada.nroFormulario) {
                    const fechaEmision = ordenPagoCreada.fechaEmision || new Date().toISOString();
                    const fechaVencimiento = ordenPagoCreada.fechaVencimiento || ordenPagoCreada.fechaVenc || fechaEmision;
                    
                    // Calcular si está vencido
                    const hoy = new Date();
                    hoy.setHours(0, 0, 0, 0);
                    const fechaVto = new Date(fechaVencimiento);
                    fechaVto.setHours(0, 0, 0, 0);
                    const esVencido = fechaVto < hoy;
                    
                    // Formatear número de comprobante como "OPX-XXXX-XXXXXXX"
                    const nroSuc = ordenPagoCreada.nroSuc || formulario.nroSuc || 0;
                    const nroComprobante = ordenPagoCreada.nroComprobante || ordenPagoCreada.nroFormulario || 0;
                    const formatPart = (value: number | string, length: number) => {
                        const numeric = Number(value);
                        if (!Number.isNaN(numeric)) {
                            return numeric.toString().padStart(length, '0');
                        }
                        return String(value).padStart(length, '0');
                    };
                    const sucursal = formatPart(nroSuc, 4);
                    const comprobante = formatPart(nroComprobante, 8);
                    const numeroFormateado = `OPX-${sucursal}-${comprobante}`;
                    
                    // Calcular el saldo total de la orden de pago (negativo porque es una deuda)
                    const saldoTotal = mediosDeCobro.reduce((sum, medio) => sum + (Number(medio.importe) || 0), 0);
                    
                    // Crear el comprobante de deuda para la orden de pago
                    const nuevoComprobante: ComprobanteDeuda = {
                        id: ordenPagoCreada.nroFormulario + 1000000, // ID único para evitar conflictos
                        nroComprobante: numeroFormateado,
                        tipoComprobante: '4 - Orden Pago',
                        fechaEmision: fechaEmision,
                        fechaVencimiento: fechaVencimiento,
                        importe: saldoTotal,
                        saldo: -saldoTotal, // Saldo negativo porque es una deuda
                        vencido: esVencido,
                        nroFormulario: ordenPagoCreada.nroFormulario
                    };
                    
                    // Agregar a la lista de comprobantes de deuda
                    setComprobantesDeuda(prevComprobantes => {
                        // Verificar que no exista ya
                        const existe = prevComprobantes.some(c => c.nroFormulario === nuevoComprobante.nroFormulario);
                        if (existe) {
                            return prevComprobantes;
                        }
                        
                        const nuevosComprobantes = [nuevoComprobante, ...prevComprobantes];
                        
                        // Recalcular saldos totales
                        const total = nuevosComprobantes.reduce((sum, c) => sum + (c.saldo || 0), 0);
                        const comprobantesVencidos = nuevosComprobantes.filter(c => c.vencido === true);
                        const vencido = comprobantesVencidos.reduce((sum, c) => sum + (c.saldo || 0), 0);
                        const comprobantesVigentes = nuevosComprobantes.filter(c => c.vencido === false);
                        const vigente = comprobantesVigentes.reduce((sum, c) => sum + (c.saldo || 0), 0);
                        
                        setSaldoTotal(total);
                        setSaldoVencido(vencido);
                        setSaldoVigente(vigente);
                        
                        return nuevosComprobantes;
                    });
                }
                
                // Actualizar los saldos de los documentos aplicados
                if (documentosAplicar.length > 0) {
                    // Capturar los datos necesarios antes de actualizar el estado
                    const idsSeleccionados = Array.from(comprobantesSeleccionados);
                    const comprobantesActuales = [...comprobantesDeuda];
                    
                    console.log('🔄 Iniciando actualización de saldos...');
                    console.log('📋 Documentos aplicados:', documentosAplicar);
                    console.log('📋 IDs seleccionados:', idsSeleccionados);
                    console.log('📋 Comprobantes deuda actuales:', comprobantesActuales.map(c => ({
                        id: c.id,
                        nroComprobante: c.nroComprobante,
                        nroFormulario: c.nroFormulario,
                        saldo: c.saldo
                    })));
                    
                    // Crear un mapa de documentos aplicados por nroFormulario para búsqueda rápida
                    const docsAplicadosMap = new Map<number, number>();
                    documentosAplicar.forEach(doc => {
                        docsAplicadosMap.set(doc.nroIdformulario, doc.montoCancelado);
                    });
                    
                    setComprobantesDeuda(prevComprobantes => {
                        const comprobantesActualizados = prevComprobantes.map(comp => {
                            // Verificar si este comprobante fue seleccionado
                            const fueSeleccionado = idsSeleccionados.includes(comp.id);
                            
                            if (fueSeleccionado && comp.nroFormulario) {
                                console.log(`🔍 Verificando comprobante seleccionado: ${comp.nroComprobante} (ID: ${comp.id}, nroFormulario: ${comp.nroFormulario})`);
                                
                                // Buscar el monto cancelado para este comprobante
                                const montoCancelado = docsAplicadosMap.get(comp.nroFormulario);
                                
                                if (montoCancelado !== undefined) {
                                    // Descontar el monto pagado del saldo
                                    const saldoAnterior = comp.saldo || 0;
                                    const nuevoSaldo = Math.max(0, saldoAnterior - montoCancelado);
                                    
                                    console.log(`✅ Descontando pago de factura ${comp.nroComprobante}:`, {
                                        saldoAnterior: saldoAnterior,
                                        montoCancelado: montoCancelado,
                                        nuevoSaldo: nuevoSaldo,
                                        diferencia: saldoAnterior - nuevoSaldo
                                    });
                                    
                                    return {
                                        ...comp,
                                        saldo: nuevoSaldo
                                    };
                                } else {
                                    console.warn(`⚠️ No se encontró monto cancelado para comprobante ${comp.nroComprobante} (nroFormulario: ${comp.nroFormulario})`);
                                    console.warn(`   Documentos aplicados disponibles:`, Array.from(docsAplicadosMap.entries()).map(([nroId, monto]) => ({
                                        nroIdformulario: nroId,
                                        montoCancelado: monto
                                    })));
                                }
                            }
                            
                            return comp;
                        }).filter(comp =>
                            esNcVisibleEnOrdenPago(comp, null) || (comp.saldo || 0) > 0
                        ); // Mantener NC; eliminar débitos con saldo 0
                        
                        // Recalcular saldos totales solo con débitos
                        const debitosActualizados = comprobantesActualizados.filter((c) =>
                            esComprobanteImputableOrdenPago(c)
                        );
                        const total = debitosActualizados.reduce((sum, c) => sum + (c.saldo || 0), 0);
                        const comprobantesVencidos = debitosActualizados.filter(c => c.vencido === true);
                        const vencido = comprobantesVencidos.reduce((sum, c) => sum + (c.saldo || 0), 0);
                        const comprobantesVigentes = debitosActualizados.filter(c => c.vencido === false);
                        const vigente = comprobantesVigentes.reduce((sum, c) => sum + (c.saldo || 0), 0);
                        
                        setSaldoTotal(total);
                        setSaldoVencido(vencido);
                        setSaldoVigente(vigente);
                        
                        console.log('💰 Saldos actualizados después del pago:', {
                            saldoTotal: total,
                            saldoVencido: vencido,
                            saldoVigente: vigente,
                            comprobantesActualizados: comprobantesActualizados.length
                        });
                        
                        return comprobantesActualizados;
                    });
                    
                    // Limpiar selecciones y montos a aplicar DESPUÉS de actualizar los comprobantes
                    setTimeout(() => {
                        setComprobantesSeleccionados(new Set());
                        setMontosAplicar(new Map());
                    }, 100);
                }
                
                const cantidadFacturas = documentosAplicar.length;
                const mensaje = cantidadFacturas > 0 
                    ? `Pago procesado exitosamente. ${cantidadFacturas} factura${cantidadFacturas > 1 ? 's' : ''} pagada${cantidadFacturas > 1 ? 's' : ''}.`
                    : 'OrdenPago creado exitosamente';
                const saldoPosterior = ordenPagoCreada?.saldoCuentaCorriente;
                const mensajeSaldo = saldoPosterior != null
                    ? ` Saldo cuenta corriente: ${formatCurrency(Number(saldoPosterior))}.`
                    : '';

                enqueueSnackbar(mensaje + mensajeSaldo, { variant: 'success' });
                
                // Esperar un momento para que la actualización se refleje antes de navegar
                setTimeout(() => {
                    navigate('/tesoreria/orden-pago');
                }, 500);
            } catch (apiError: any) {
                // Interpretar errores del servidor y mostrarlos claramente
                const status = apiError?.response?.status;
                const data = apiError?.response?.data;
                if (data?.errors && typeof data.errors === 'object') {
                    try {
                        const msgs: string[] = [];
                        for (const k of Object.keys(data.errors)) {
                            const val = data.errors[k];
                            if (Array.isArray(val)) msgs.push(`${k}: ${val.join(' - ')}`);
                            else if (val) msgs.push(`${k}: ${String(val)}`);
                        }
                        enqueueSnackbar(msgs.join(' | ') || data.title || 'Error de validación', { variant: 'error' });
                        return;
                    } catch {}
                }
                if (status === 400) {
                    enqueueSnackbar(data?.title || 'Solicitud inválida (400). Revise los datos ingresados.', { variant: 'error' });
                    return;
                }
                if (status === 403) {
                    notifyPermissionDenied(enqueueSnackbar, apiError);
                    return;
                }
                if (status === 401) {
                    enqueueSnackbar('No autorizado para crear OrdenPagos. Verifique su sesión.', { variant: 'error' });
                    return;
                }
                if (status === 404) {
                    enqueueSnackbar('Endpoint no encontrado (404). Consulte con soporte.', { variant: 'error' });
                    return;
                }
                if (status === 500) {
                    enqueueSnackbar('Error interno del servidor (500). Intente nuevamente.', { variant: 'error' });
                    return;
                }
                throw apiError; // fallback
            }
        } catch (error: any) {
            console.error('Error:', error);
            const data = error?.response?.data;
            if (data?.errors && typeof data.errors === 'object') {
                try {
                    const msgs: string[] = [];
                    for (const k of Object.keys(data.errors)) {
                        const val = data.errors[k];
                        if (Array.isArray(val)) {
                            msgs.push(`${k}: ${val.join(' - ')}`);
                        } else if (val) {
                            msgs.push(`${k}: ${String(val)}`);
                        }
                    }
                    const message = msgs.join(' | ');
                    enqueueSnackbar(message || data.title || 'Error de validación', { variant: 'error' });
                    return;
                } catch {}
            }
            enqueueSnackbar(data?.title || data || 'Error al crear el OrdenPago', { variant: 'error' });
        }
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
            <Card
                sx={{
                    bgcolor: '#ffffff',
                    borderRadius: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={1.5} mb={2.5} flexWrap="wrap">
                        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tesoreria/orden-pago')} size="small" sx={{ textTransform: 'none' }}>
                            Órdenes de pago
                        </Button>
                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                        <Typography
                            variant={isSmallScreen ? 'h5' : 'h4'}
                            sx={{
                                fontWeight: 600,
                                color: '#1976d2',
                                fontSize: { xs: '1.5rem', sm: '2rem' },
                            }}
                        >
                            Nueva Orden de Pago
                        </Typography>
                        {nrosCargaDesdeCartera.length > 0 && (
                            <Chip
                                color="info"
                                size="small"
                                label={`${nrosCargaDesdeCartera.length} cheque${nrosCargaDesdeCartera.length > 1 ? 's' : ''} para endosar`}
                                onDelete={() => {
                                    setNrosCargaDesdeCartera([]);
                                    preseleccionChequesOpRef.current = false;
                                }}
                            />
                        )}
                    </Stack>
                    <form onSubmit={handleSubmit}>
                <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
                    <Grid item xs={12} md={8}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Box>
                                    <Typography
                                        variant="overline"
                                        color="text.secondary"
                                        display="block"
                                        mb={0.75}
                                        sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}
                                    >
                                        Proveedor
                                    </Typography>
                                    {!(formulario.nroProveedor > 0 && formulario.nombre) ? (
                                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <Box sx={{ flex: 1, minWidth: 200 }}>
                                                <ProveedorInlineSearchField
                                                    label=""
                                                    placeholder="Nº proveedor, nombre o CUIT"
                                                    helperText=""
                                                    disabled={ProveedorBloqueado}
                                                    onSelect={handleProveedorSelect}
                                                />
                                            </Box>
                                            <Button
                                                type="button"
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<PersonSearch color="primary" />}
                                                disabled={ProveedorBloqueado}
                                                onClick={() => setOpenProveedorSearch(true)}
                                                sx={{
                                                    py: 1,
                                                    px: 2,
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    whiteSpace: 'nowrap',
                                                    flexShrink: 0,
                                                    bgcolor: 'background.paper',
                                                    borderColor: 'divider',
                                                    '&:hover': {
                                                        bgcolor: 'action.hover',
                                                        borderColor: 'primary.main',
                                                    },
                                                }}
                                            >
                                                Seleccionar proveedor
                                            </Button>
                                        </Stack>
                                    ) : (
                                        <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                                                    <Box flex={1} minWidth={0}>
                                                        <Stack direction="row" alignItems="center" spacing={1} mb={0.5} flexWrap="wrap" useFlexGap>
                                                            <Typography variant="subtitle1" fontWeight={600}>
                                                                {formulario.nombre}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                #{formulario.nroProveedor}
                                                            </Typography>
                                                        </Stack>
                                                        {formulario.cuit ? (
                                                            <Typography variant="body2" color="text.secondary">
                                                                CUIT: {formulario.cuit}
                                                            </Typography>
                                                        ) : null}
                                                        {formulario.tipoIVA ? (
                                                            <Box mt={0.75}>
                                                                <Chip
                                                                    label={TIPO_IVA_LABELS[formulario.tipoIVA] ?? formulario.tipoIVA}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                                                />
                                                            </Box>
                                                        ) : null}
                                                    </Box>
                                                    {!ProveedorBloqueado && (
                                                        <Stack direction="row" spacing={0} flexShrink={0}>
                                                            <Tooltip title="Cambiar proveedor">
                                                                <IconButton size="small" onClick={() => setOpenProveedorSearch(true)} aria-label="Cambiar proveedor">
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Quitar proveedor">
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={handleProveedorClear}
                                                                    aria-label="Quitar proveedor"
                                                                >
                                                                    <DeleteOutline fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Stack>
                                                    )}
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    )}
                                    {ProveedorBloqueado && (
                                        <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
                                            No se puede modificar el proveedor cuando hay comprobantes aplicados o medios de pago asignados
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
                            <Box
                                sx={{
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    p: 2,
                                    bgcolor: 'background.paper',
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    textTransform="uppercase"
                                    letterSpacing={0.5}
                                    display="block"
                                    mb={1.5}
                                >
                                    Datos de emisión
                                </Typography>
                                <Stack spacing={1.5}>
                                    <SearchableSelect
                                        label="Sucursal"
                                        size="small"
                                        required
                                        loading={loadingSucursales}
                                        options={sucursales.map((sucursal) => ({
                                            value: sucursal.nroSuc,
                                            label: sucursal.nombreSucursal?.trim()
                                                ? `${sucursal.nroSuc} — ${sucursal.nombreSucursal.trim()}`
                                                : `Suc. ${sucursal.nroSuc}`,
                                        }))}
                                        value={formulario.nroSuc > 0 ? formulario.nroSuc : null}
                                        onChange={(value) => onSucursalChange(Number(value) || 0)}
                                    />
                                    <GrupoCajaSelect
                                        usuario={loggedUsername}
                                        value={grupoCajaSeleccionado}
                                        onChange={handleGrupoCajaChange}
                                        locked={grupoCajaBloqueado}
                                        required
                                    />
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Fecha de Emisión"
                                        name="fechaEmision"
                                        type="date"
                                        value={formulario.fechaEmision}
                                        onChange={handleFormularioChange}
                                        InputLabelProps={{ shrink: true }}
                                        required
                                    />
                                </Stack>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <ObservacionesConNotaProveedor
                            nroProveedor={formulario.nroProveedor > 0 ? formulario.nroProveedor : null}
                        >
                            <TextField
                                size="small"
                                fullWidth
                                label="Observaciones"
                                name="descripcion"
                                value={formulario.descripcion}
                                onChange={handleFormularioChange}
                                multiline
                                minRows={2}
                                inputProps={{ maxLength: 2000 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                                            <NotesIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </ObservacionesConNotaProveedor>
                    </Grid>
                </Grid>

                <Grid container spacing={2} sx={{ mb: 1 }} alignItems="flex-start">
                    <Grid item xs={12} md={6}>
                        <Card sx={{ boxShadow: 2 }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                    display="block"
                                    mb={1.5}
                                    sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}
                                >
                                    Comprobantes adeudados
                                </Typography>
                            {/* Información de Saldos */}
                            <Box sx={{ mb: { xs: 2, sm: 3 }, bgcolor: 'grey.50', p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                                <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                                    <Grid item xs={12} sm={4}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                Vigente:
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: saldoVigente < 0 ? 'success.main' : 'text.primary', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                                {formatCurrency(saldoVigente)}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                Vencido:
                                            </Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: saldoVencido < 0 ? 'success.main' : 'error.main', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                                {formatCurrency(saldoVencido)}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                Saldo Total:
                                            </Typography>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: 'white',
                                                    bgcolor: saldoTotal < 0 ? 'success.main' : 'error.main',
                                                    borderRadius: 1.5,
                                                    px: 1.5,
                                                    py: 0.75,
                                                    display: 'inline-block',
                                                    minWidth: { xs: '100%', sm: '170px' },
                                                    textAlign: 'center',
                                                    fontSize: { xs: '1rem', sm: '1.25rem' }
                                                }}
                                            >
                                                {formatCurrency(saldoTotal)}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Sección de Comprobantes Seleccionados */}
                            {comprobantesSeleccionados.size > 0 && (
                                <Box sx={{ mb: 3 }}>
                                    <Card sx={{ bgcolor: 'rgba(25, 118, 210, 0.08)', borderRadius: 2, boxShadow: 2 }}>
                                        <CardContent sx={{ p: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                                                        Comprobantes Seleccionados
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {comprobantesSeleccionados.size} comprobante{comprobantesSeleccionados.size > 1 ? 's' : ''}
                                                    </Typography>
                                                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                                                        Total: {formatCurrency(totalAplicado)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setComprobantesSeleccionados(new Set());
                                                            setMontosAplicar(new Map());
                                                            enqueueSnackbar('Comprobantes deseleccionados', { variant: 'info' });
                                                        }}
                                                        sx={{
                                                            color: 'text.secondary',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(0, 0, 0, 0.04)'
                                                            }
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Box>
                            )}

                            {/* Barra de búsqueda y botón Pagar Todo */}
                            <Box sx={{ display: 'flex', gap: 2, mb: { xs: 2, sm: 3 }, flexDirection: { xs: 'column', sm: 'row' } }}>
                                <TextField
                                    fullWidth
                                    placeholder="Buscar comprobantes..."
                                    value={searchComprobante}
                                    onChange={(e) => setSearchComprobante(e.target.value)}
                                    size={isSmallScreen ? "small" : "medium"}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize={isSmallScreen ? "small" : "medium"} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        flex: 1,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            bgcolor: 'grey.100'
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handlePagarTodo}
                                    fullWidth={isSmallScreen}
                                    sx={{
                                        minWidth: { xs: '100%', sm: '150px' },
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: { xs: '0.875rem', sm: '1rem' },
                                        py: { xs: 1.5, sm: 1 }
                                    }}
                                    disabled={
                                        comprobantesDeuda.filter((c) => esComprobanteImputableOrdenPago(c)).length === 0 ||
                                        saldoTotal === 0
                                    }
                                >
                                    Pagar Todo
                                </Button>
                            </Box>

                            {/* Lista de Comprobantes */}
                            {loadingComprobantes ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        Cargando comprobantes...
                                    </Typography>
                                    {formulario.nroProveedor > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            Proveedor: {formulario.nroProveedor}
                                        </Typography>
                                    )}
                                </Box>
                            ) : comprobantesDeuda.length === 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        No hay comprobantes disponibles
                                    </Typography>
                                    {formulario.nroProveedor > 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            Proveedor: {formulario.nroProveedor}
                                        </Typography>
                                    )}
                                    {formulario.nroProveedor === 0 && (
                                        <Typography variant="caption" color="text.secondary">
                                            Seleccione un Proveedor para ver sus comprobantes de deuda
                                        </Typography>
                                    )}
                                </Box>
                            ) : (
                                <Box sx={{ maxHeight: { xs: '50vh', sm: 400 }, overflowY: 'auto', pr: 1 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {comprobantesDeuda
                                        .filter(comp => 
                                            !searchComprobante || 
                                            comp.nroComprobante.toLowerCase().includes(searchComprobante.toLowerCase()) ||
                                            comp.tipoComprobante.toLowerCase().includes(searchComprobante.toLowerCase())
                                        )
                                        .sort((a, b) => {
                                            const aNc = esNcVisibleEnOrdenPago(a, null);
                                            const bNc = esNcVisibleEnOrdenPago(b, null);
                                            if (aNc !== bNc) return aNc ? 1 : -1;

                                            // Ordenar de mayor tiempo vencido a menor tiempo vencido
                                            const hoy = new Date();
                                            hoy.setHours(0, 0, 0, 0); // Normalizar a medianoche
                                            
                                            const fechaVencA = new Date(a.fechaVencimiento);
                                            fechaVencA.setHours(0, 0, 0, 0);
                                            const fechaVencB = new Date(b.fechaVencimiento);
                                            fechaVencB.setHours(0, 0, 0, 0);
                                            
                                            const estaVencidaA = fechaVencA < hoy;
                                            const estaVencidaB = fechaVencB < hoy;
                                            
                                            // Si ambas están vencidas, ordenar por tiempo vencido (mayor tiempo vencido primero)
                                            if (estaVencidaA && estaVencidaB) {
                                                const tiempoVencidoA = hoy.getTime() - fechaVencA.getTime();
                                                const tiempoVencidoB = hoy.getTime() - fechaVencB.getTime();
                                                // Orden descendente: mayor tiempo vencido primero
                                                return tiempoVencidoB - tiempoVencidoA;
                                            }
                                            
                                            // Si solo A está vencida, A va primero
                                            if (estaVencidaA && !estaVencidaB) {
                                                return -1;
                                            }
                                            
                                            // Si solo B está vencida, B va primero
                                            if (!estaVencidaA && estaVencidaB) {
                                                return 1;
                                            }
                                            
                                            // Si ninguna está vencida, ordenar por fecha de vencimiento (más lejanas primero)
                                            return fechaVencB.getTime() - fechaVencA.getTime();
                                        })
                                        .map((comp, idx, arr) => {
                                            const esNc = esNcVisibleEnOrdenPago(comp, null);
                                            const esImputable = esComprobanteImputableOrdenPago(comp);
                                            const estaSeleccionado = !esNc && comprobantesSeleccionados.has(comp.id);
                                            const estaExpandido = !esNc && comprobantesExpandidos.has(comp.id);
                                            const numeroFormateado = formatNumeroComprobante(comp.nroComprobante, comp.tipoComprobante);
                                            // Saldo acumulado de línea: solo débitos
                                            const saldoAcumuladoLinea = arr
                                                .slice(0, idx + 1)
                                                .filter((it) => esComprobanteImputableOrdenPago(it))
                                                .reduce((sum, it) => sum + (Number(it.saldo ?? it.importe ?? 0) || 0), 0);

                                            if (esNc) {
                                                return (
                                                    <Card
                                                        key={comp.id}
                                                        variant="outlined"
                                                        sx={{
                                                            borderRadius: 2,
                                                            bgcolor: 'rgba(46, 125, 50, 0.04)',
                                                            borderWidth: 1.5,
                                                            borderStyle: 'solid',
                                                            borderColor: 'success.light',
                                                            '&:hover': { boxShadow: 2 },
                                                        }}
                                                    >
                                                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, position: 'relative' }}>
                                                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'nowrap' }}>
                                                                <Box sx={{ width: 21, flexShrink: 0 }} />
                                                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                                        <Typography
                                                                            variant="subtitle1"
                                                                            sx={{
                                                                                fontWeight: 700,
                                                                                color: 'success.main',
                                                                                mb: 0,
                                                                                lineHeight: 1.15,
                                                                                fontSize: '0.95rem',
                                                                            }}
                                                                        >
                                                                            {numeroFormateado}
                                                                        </Typography>
                                                                        <Chip
                                                                            label="NC"
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor: 'success.main',
                                                                                color: 'white',
                                                                                fontWeight: 600,
                                                                                height: 18,
                                                                                fontSize: '0.7rem',
                                                                                borderRadius: '12px',
                                                                                px: 0.75,
                                                                            }}
                                                                        />
                                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                                            {comp.tipoComprobante.includes(' - ')
                                                                                ? comp.tipoComprobante
                                                                                : `${comp.nroTipoFormulario || '3'} - Nota de Crédito`}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                                            {new Date(comp.fechaEmision).toLocaleDateString('es-AR', {
                                                                                day: '2-digit',
                                                                                month: '2-digit',
                                                                                year: 'numeric',
                                                                            })}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box sx={{ mt: 1 }}>
                                                                        <Button
                                                                            variant="outlined"
                                                                            color="success"
                                                                            size="small"
                                                                            startIcon={<AccountBalanceWalletIcon />}
                                                                            onClick={() => handleAbrirAplicarNc(comp)}
                                                                            disabled={!comp.nroFormulario}
                                                                            sx={{
                                                                                borderRadius: 2,
                                                                                textTransform: 'none',
                                                                                fontWeight: 600,
                                                                            }}
                                                                        >
                                                                            Aplicar
                                                                        </Button>
                                                                    </Box>
                                                                </Box>
                                                                <Box sx={{ textAlign: 'right', minWidth: 90, flexShrink: 0 }}>
                                                                    <Typography
                                                                        variant="subtitle1"
                                                                        sx={{
                                                                            fontWeight: 700,
                                                                            color: 'success.main',
                                                                            fontSize: '0.95rem',
                                                                            lineHeight: 1.15,
                                                                        }}
                                                                    >
                                                                        {formatCurrency(Math.abs(comp.saldo || 0))}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                                        Saldo a favor
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                );
                                            }
                                            
                                            return (
                                                <Card 
                                                    key={comp.id}
                                                    variant="outlined" 
                                                    sx={{ 
                                                        borderRadius: 2,
                                                        bgcolor: 'white',
                                                        borderWidth: 1.5,
                                                        borderStyle: 'solid',
                                                        borderColor: estaSeleccionado ? 'primary.main' : 'divider',
                                                        '&:hover': {
                                                            boxShadow: 2
                                                        }
                                                    }}
                                                >
                                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, position: 'relative' }}>
                                                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'nowrap' }}>
                                                            <Checkbox
                                                                checked={estaSeleccionado}
                                                                onChange={() => handleToggleComprobante(comp.id)}
                                                                disabled={!esImputable}
                                                                sx={{
                                                                    p: 0,
                                                                    '& .MuiSvgIcon-root': {
                                                                        fontSize: 21
                                                                    }
                                                                }}
                                                                icon={<RadioButtonUncheckedIcon sx={{ color: 'grey.400' }} />}
                                                                checkedIcon={<RadioButtonCheckedIcon sx={{ color: 'primary.main' }} />}
                                                            />
                                                            
                                                            {/* Contenido del comprobante */}
                                                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                                    <Typography 
                                                                        variant="subtitle1" 
                                                                        sx={{ 
                                                                            fontWeight: 700, 
                                                                            color: estaSeleccionado ? 'success.main' : 'text.primary',
                                                                            mb: 0,
                                                                            lineHeight: 1.15,
                                                                            fontSize: '0.95rem'
                                                                        }}
                                                                    >
                                                                        {numeroFormateado}
                                                                    </Typography>

                                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                                        {comp.tipoComprobante.includes(' - ') 
                                                                            ? comp.tipoComprobante 
                                                                            : `${comp.id || comp.tipoComprobante.substring(0, 1)} - ${comp.tipoComprobante}`}
                                                                    </Typography>

                                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                                        {new Date(comp.fechaEmision).toLocaleDateString('es-AR', {
                                                                            day: '2-digit',
                                                                            month: '2-digit',
                                                                            year: 'numeric'
                                                                        })}
                                                                    </Typography>

                                                                    {/* Badge Vencido/Vigente */}
                                                                    {comp.vencido ? (
                                                                        <Chip
                                                                            label="Vencido"
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor: 'error.main',
                                                                                color: 'white',
                                                                                fontWeight: 600,
                                                                                height: 18,
                                                                                fontSize: '0.7rem',
                                                                                borderRadius: '12px',
                                                                                px: 0.75
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <Chip
                                                                            label="Vigente"
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor: 'success.main',
                                                                                color: 'white',
                                                                                fontWeight: 600,
                                                                                height: 18,
                                                                                fontSize: '0.7rem',
                                                                                borderRadius: '12px',
                                                                                px: 0.75
                                                                            }}
                                                                        />
                                                                    )}
                                                                    
                                                                    {/* Botón Ver detalles / Ver menos */}
                                                                    <Button
                                                                        size="small"
                                                                        endIcon={<ExpandMoreIcon sx={{ 
                                                                            transform: estaExpandido ? 'rotate(180deg)' : 'rotate(0deg)',
                                                                            transition: 'transform 0.3s',
                                                                            fontSize: '0.9rem'
                                                                        }} />}
                                                                        onClick={() => handleToggleDetalles(comp.id)}
                                                                        sx={{
                                                                            color: 'primary.main',
                                                                            textTransform: 'none',
                                                                            p: 0.25,
                                                                            minWidth: 'auto',
                                                                            fontSize: '0.75rem',
                                                                            fontWeight: 500,
                                                                            '&:hover': {
                                                                                bgcolor: 'rgba(25, 118, 210, 0.08)'
                                                                            }
                                                                        }}
                                                                    >
                                                                        {estaExpandido ? 'Ver menos' : 'Ver detalles'}
                                                                    </Button>
                                                                </Box>
                                                                
                                                                {/* Detalles expandidos */}
                                                                {estaExpandido && (
                                                                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                                                                        {/* Importe */}
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                                            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                                                                Importe:
                                                                            </Typography>
                                                                            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                                                                {formatCurrency(comp.importe)}
                                                                            </Typography>
                                                                        </Box>
                                                                        
                                                                        {/* Vencimiento */}
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                                            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                                                                Vencimiento:
                                                                            </Typography>
                                                                            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                                                                {comp.fechaVencimiento ? new Date(comp.fechaVencimiento).toLocaleDateString('es-AR', {
                                                                                    day: '2-digit',
                                                                                    month: '2-digit',
                                                                                    year: 'numeric'
                                                                                }) : 'N/A'}
                                                                            </Typography>
                                                                        </Box>
                                                                        
                                                                        {/* Aplicar - Solo se muestra si la factura está seleccionada */}
                                                                        {estaSeleccionado && (
                                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                                                                    Aplicar:
                                                                                </Typography>
                                                                                <TextField
                                                                                    type="number"
                                                                                    size="small"
                                                                                    value={montosAplicar.get(comp.id) ?? ''}
                                                                                    onChange={(e) => {
                                                                                        const valorIngresado = e.target.value;
                                                                                        
                                                                                        // Si el campo está vacío, permitir que se borre completamente
                                                                                        if (valorIngresado === '' || valorIngresado === null || valorIngresado === undefined) {
                                                                                            setMontosAplicar(prev => {
                                                                                                const nuevo = new Map(prev);
                                                                                                nuevo.delete(comp.id);
                                                                                                return nuevo;
                                                                                            });
                                                                                            return;
                                                                                        }
                                                                                        
                                                                                        // Convertir a número
                                                                                        const nuevoMonto = parseFloat(valorIngresado);
                                                                                        
                                                                                        // Si no es un número válido, no actualizar
                                                                                        if (isNaN(nuevoMonto)) {
                                                                                            return;
                                                                                        }
                                                                                        
                                                                                        const importeMaximo = montoImputableComprobante(comp);
                                                                                        
                                                                                        // Si el monto ingresado es mayor al pendiente, no actualizar y mostrar advertencia
                                                                                        if (nuevoMonto > importeMaximo) {
                                                                                            enqueueSnackbar(`El monto a aplicar no puede ser mayor al saldo pendiente (${formatCurrency(importeMaximo)})`, { variant: 'warning' });
                                                                                            return;
                                                                                        }
                                                                                        
                                                                                        // Si el monto es válido (menor o igual al importe), actualizarlo
                                                                                        setMontosAplicar(prev => {
                                                                                            const nuevo = new Map(prev);
                                                                                            nuevo.set(comp.id, roundMoney(nuevoMonto));
                                                                                            return nuevo;
                                                                                        });
                                                                                    }}
                                                                                    onBlur={() => {
                                                                                        // Validar al perder el foco
                                                                                        const valorActual = montosAplicar.get(comp.id);
                                                                                        const importeMaximo = montoImputableComprobante(comp);
                                                                                        if (valorActual === undefined || valorActual === null) {
                                                                                            return;
                                                                                        }
                                                                                        
                                                                                        // Si el valor es mayor al importe, establecerlo al máximo permitido
                                                                                        if (valorActual > importeMaximo) {
                                                                                            setMontosAplicar(prev => {
                                                                                                const nuevo = new Map(prev);
                                                                                                nuevo.set(comp.id, roundMoney(importeMaximo));
                                                                                                return nuevo;
                                                                                            });
                                                                                            enqueueSnackbar(`El monto se ajustó al máximo permitido: ${formatCurrency(importeMaximo)}`, { variant: 'info' });
                                                                                        }
                                                                                    }}
                                                                                    inputProps={{
                                                                                        min: 0,
                                                                                        max: montoImputableComprobante(comp),
                                                                                        step: 0.01
                                                                                    }}
                                                                                    placeholder="0"
                                                                                    sx={{
                                                                                        width: '150px',
                                                                                        '& .MuiOutlinedInput-root': {
                                                                                            borderRadius: 1
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            </Box>
                                                                        )}
                                                                        
                                                                        {/* Saldo Acumulado - acumulado progresivo por línea (responsive) */}
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                flexDirection: { xs: 'column', sm: 'row' },
                                                                                justifyContent: { xs: 'flex-start', sm: 'space-between' },
                                                                                alignItems: { xs: 'flex-start', sm: 'center' },
                                                                                mb: 1.5,
                                                                                gap: { xs: 0.25, sm: 0 },
                                                                                width: '100%'
                                                                            }}
                                                                        >
                                                                            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                                                                                Saldo Acumulado:
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="body2"
                                                                                color={saldoAcumuladoLinea < 0 ? 'success.main' : 'error.main'}
                                                                                sx={{
                                                                                    fontWeight: 700,
                                                                                    textAlign: { xs: 'left', sm: 'right' },
                                                                                    mt: { xs: 0.25, sm: 0 },
                                                                                    width: { xs: '100%', sm: 'auto' },
                                                                                    overflowWrap: 'anywhere',
                                                                                    wordBreak: 'break-word'
                                                                                }}
                                                                            >
                                                                                {formatCurrency(saldoAcumuladoLinea)}
                                                                            </Typography>
                                                                        </Box>
                                                                        
                                                                        {/* Botón Detalles */}
                                                                        <Box sx={{ mt: 1.5 }}>
                                                                            <Button
                                                                                variant="contained"
                                                                                color="primary"
                                                                                startIcon={<DescriptionIcon />}
                                                                                onClick={() => handleVerDetalles(comp)}
                                                                                disabled={!comp.nroFormulario}
                                                                                sx={{
                                                                                    borderRadius: 2,
                                                                                    textTransform: 'none',
                                                                                    fontWeight: 600,
                                                                                    px: 3,
                                                                                    py: 0.75,
                                                                                    width: '100%'
                                                                                }}
                                                                            >
                                                                                Detalles
                                                                            </Button>
                                                                        </Box>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                            
                                                            {/* Monto a la derecha */}
                                                            <Box sx={{
                                                                textAlign: 'right',
                                                                minWidth: '90px',
                                                                flexShrink: 0
                                                            }}>
                                                                <Typography 
                                                                    variant="subtitle1" 
                                                                    sx={{ 
                                                                        fontWeight: 700, 
                                                                        color: comp.saldo < 0 ? 'success.main' : 'error.main',
                                                                        mb: 0,
                                                                        fontSize: '0.95rem',
                                                                        lineHeight: 1.15
                                                                    }}
                                                                >
                                                                    {formatCurrency(comp.saldo)}
                                                                </Typography>
                                                                <Typography 
                                                                    variant="caption" 
                                                                    color="text.secondary"
                                                                    sx={{ fontSize: '0.7rem' }}
                                                                >
                                                                    Saldo
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            );
                                        })}
                                </Box>
                                </Box>
                            )}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card sx={{ boxShadow: 2 }}>
                            <CardContent sx={{ p: 2 }}>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                    display="block"
                                    mb={1.5}
                                    sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}
                                >
                                    Medios de pago
                                </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2, gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleOpenPagoDialog}
                                disabled={!(Number(formulario.nroProveedor) > 0) || !formulario.nombre}
                                fullWidth={isSmallScreen}
                                sx={{
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    py: { xs: 1.5, sm: 1 }
                                }}
                            >
                                Agregar Pago
                            </Button>
                        </Box>

                        <Grid container spacing={2}>
                            {detallesPago.map((pago, index) => (
                                <Grid item xs={12} key={index}>
                                    <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 0 }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Box>
                                                    <Typography sx={{ fontWeight: 800, color: 'primary.main' }}>
                                                        {labelTipoPagoOp(pago.tipoPago)}
                                                    </Typography>
                                                    {pago.tipoPago === 'Transferencia' && (
                                                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                            {pago.medioLabel || ''}
                                                        </Typography>
                                                    )}
                                                    {pago.tipoPago === 'Efectivo' && (
                                                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                            EFECTIVO CAJA CHICA
                                                        </Typography>
                                                    )}
                                                    {pago.tipoPago === 'Cheque' && (
                                                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                            {pago.nroCarga
                                                                ? `${pago.medioLabel || `Cheque ${pago.nroCheque || ''}`}${pago.librador ? ` · ${pago.librador}` : ''}`
                                                                : 'CAJA CHICA CHEQUES DE TERCERO'}
                                                        </Typography>
                                                    )}
                                                    {pago.tipoPago === 'ChequePropio' && (
                                                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                            {`${pago.medioLabel || ''}${pago.nroCheque ? ` · Cheque ${pago.nroCheque}` : ''}${pago.banco ? ` · ${pago.banco}` : ''}`.trim() || 'CHEQUE PROPIO'}
                                                        </Typography>
                                                    )}
                                                    {pago.tipoPago === 'Tarjeta' && (
                                                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                            {pago.medioLabel || 'CAJA TARJETA'}
                                                        </Typography>
                                                    )}
                                                    {pago.tipoPago === 'Otros' && (
                                                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                            {pago.medioLabel || 'OTROS'}
                                                        </Typography>
                                                    )}
                                                    {!!pago.detalle && (
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', display: 'block', mt: 0.5 }}>
                                                            {pago.detalle}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography sx={{ fontWeight: 800, color: 'success.main' }}>
                                                        {formatCurrency(pago.importe || 0)}
                                                    </Typography>
                                                    {pago.tipoPago !== 'Cheque' && !(pago.nroCarga != null && pago.nroCarga > 0) && (
                                                        <Tooltip title="Editar medio">
                                                            <IconButton
                                                                onClick={() => { void handleEditarPago(index); }}
                                                                size="small"
                                                                sx={{ color: 'text.secondary' }}
                                                                aria-label="Editar medio"
                                                            >
                                                                <Edit />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <IconButton
                                                        onClick={() => removePago(index)}
                                                        size="small"
                                                        sx={{ color: 'text.secondary' }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                            {detallesPago.length === 0 && (
                                <Grid item xs={12}>
                                    <Typography variant="body2">Aún no has agregado medios de pago.</Typography>
                                </Grid>
                            )}
                        </Grid>

                        {/* Resumen tipo tarjeta */}
                        <Box sx={{ mt: 2, p: 2, borderRadius: 2, borderLeft: '6px solid', borderColor: 'primary.main', bgcolor: 'background.paper' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography color="text.secondary">Total Aplicado:</Typography>
                                <Typography color="text.secondary">{formatCurrency(totalAplicado)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography color="text.secondary">Total Medios:</Typography>
                                <Typography sx={{ color: 'primary.main', fontWeight: 800 }}>{formatCurrency(totalMedios)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography color="text.secondary">Diferencia:</Typography>
                                <Typography sx={{ 
                                    color: diferenciaInsuficiente ? 'error.main' : diferenciaSinSaldo ? 'success.main' : 'warning.main', 
                                    fontWeight: 800 
                                }}>
                                    {diferenciaInsuficiente
                                        ? `- ${formatCurrency(Math.abs(diferencia))}`
                                        : diferenciaSinSaldo
                                            ? 'Sin Diferencia'
                                            : `${formatCurrency(diferencia)} A cuenta`}
                                </Typography>
                            </Box>
                            {diferenciaInsuficiente && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>
                                        Monto insuficiente. Falta: {formatCurrency(Math.abs(diferencia))}. Agregá otro medio de pago.
                                    </Typography>
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="warning"
                                fullWidth
                                disabled={
                                    totalMedios <= 0 ||
                                    diferenciaInsuficiente
                                }
                                sx={{
                                    ...(diferenciaInsuficiente && {
                                        bgcolor: 'grey.400',
                                        color: 'text.secondary',
                                        '&:hover': {
                                            bgcolor: 'grey.400'
                                        }
                                    })
                                }}
                            >
                                {diferenciaInsuficiente
                                    ? 'Monto Insuficiente'
                                    : diferenciaSinSaldo
                                        ? 'Procesar Pago'
                                        : 'Procesar Pago a Cuenta'}
                            </Button>
                        </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Modal para agregar/editar pagos */}
                <Dialog 
                    open={openPagoDialog} 
                    onClose={handleClosePagoDialog}
                    maxWidth={esChequeCartera ? "xl" : "sm"}
                    fullWidth
                    fullScreen={isSmallScreen}
                    PaperProps={{
                        sx: {
                            m: { xs: 0, sm: 2 },
                            maxHeight: { xs: '100vh', sm: '90vh' },
                            ...(esChequeCartera ? {
                                width: { xs: '100%', sm: '95%', md: '90%' },
                                maxWidth: { xs: '100%', sm: '1400px' }
                            } : {})
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' }, pb: { xs: 1, sm: 2 } }}>
                        {indicePagoEditando != null ? 'Editar Pago' : 'Agregar Pago'}
                    </DialogTitle>
                    <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                        <Box sx={{ mt: { xs: 1, sm: 2 } }}>
                            <Stepper activeStep={activeStep} sx={{ mb: { xs: 3, sm: 4 } }}>
                                <Step>
                                    <StepLabel sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Seleccionar Tipo</StepLabel>
                                </Step>
                                <Step>
                                    <StepLabel sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Completar Datos</StepLabel>
                                </Step>
                            </Stepper>

                            {activeStep === 0 ? (
                                <SearchableSelect
                                    label="Tipo de Pago"
                                    options={[...TIPO_PAGO_OPTIONS]}
                                    value={tipoPagoSeleccionado || null}
                                    onChange={(value) => {
                                        if (value) void handleTipoPagoChange(value);
                                    }}
                                />
                            ) : (
                                renderCamposPago()
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
                        <Button 
                            onClick={handleClosePagoDialog}
                            fullWidth={isSmallScreen}
                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                            Cancelar
                        </Button>
                        {/* El botón Guardar solo se muestra en el último paso (activeStep === 1) */}
                        {activeStep === 1 ? (
                            <Button 
                                onClick={handleGuardarPago}
                                variant="contained"
                                fullWidth={isSmallScreen}
                                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                                disabled={
                                    !pagoActual.tipoPago ||
                                    // Para cheques de cartera, el importe viene de cada cheque, así que no validamos pagoActual.importe
                                    (!esChequeCartera && !importePagoDialogValidoOp(pagoActual.importe, tipoPagoSeleccionado)) ||
                                    (tipoPagoSeleccionado === 'Efectivo' && !pagoActual.mcp) ||
                                    (tipoPagoSeleccionado === 'Transferencia' && (!cuentaTransferSeleccionadaId || !pagoActual.mcp)) ||
                                    (tipoPagoSeleccionado === 'Tarjeta' && (
                                        mediosTarjeta.length === 0 || !tarjetaSeleccionadaIdx
                                    )) ||
                                    (tipoPagoSeleccionado === 'Cheque' && (
                                        // Si es cheque de cartera, solo validar que haya cheques seleccionados
                                        esChequeCartera 
                                            ? chequesCarteraSeleccionados.size === 0
                                            : (
                                                (mediosCheques.length > 0 && !chequeSeleccionadoIdx) ||
                                                !pagoActual.nroCheque || !pagoActual.fechaEmision || !pagoActual.fechaVencimiento || !pagoActual.nroBanco || !pagoActual.nroSucursal || !pagoActual.nroLibrador
                                            )
                                    )) ||
                                    (tipoPagoSeleccionado === 'ChequePropio' && (
                                        cargandoCarteraChequePropio ||
                                        (mediosCheques.length > 0 && !chequeSeleccionadoIdx) ||
                                        !pagoActual.mcp ||
                                        !pagoActual.nroCheque ||
                                        !pagoActual.fechaEmision ||
                                        !pagoActual.fechaVencimiento ||
                                        !(Number(pagoActual.nroBanco) >= 1 && Number(pagoActual.nroBanco) <= 999) ||
                                        !(Number(pagoActual.nroSucursal) >= 1 && Number(pagoActual.nroSucursal) <= 999) ||
                                        !companiaLibradorId ||
                                        !String(pagoActual.librador || '').trim() ||
                                        !String(pagoActual.nroLibrador || '').trim()
                                    )) ||
                                    (tipoPagoSeleccionado === 'Otros' && (mediosOtros.length > 0 && !otrosSeleccionadoIdx))
                                }
                            >
                                Guardar
                            </Button>
                        ) : null}
                    </DialogActions>
                </Dialog>

                {/* Diálogo de selección de Grupo de Caja Chica */}
                <Dialog
                    open={openGrupoCajaDialog}
                    onClose={() => setOpenGrupoCajaDialog(false)}
                    maxWidth="sm"
                    fullWidth
                    fullScreen={isSmallScreen}
                    PaperProps={{
                        sx: {
                            m: { xs: 0, sm: 2 },
                            maxHeight: { xs: '100vh', sm: '90vh' },
                        },
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' }, pb: { xs: 1, sm: 2 } }}>
                        Seleccionar Grupo de Caja
                    </DialogTitle>
                    <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                        <Box sx={{ mt: { xs: 0.5, sm: 1 } }}>
                            <Box sx={{ maxHeight: { xs: '60vh', sm: 360 }, overflowY: 'auto', pr: 1 }} onScroll={handleGrupoCajaScroll}>
                                <Grid container spacing={2}>
                                    {gruposCaja.map((g) => (
                                        <Grid item xs={12} key={g.id}>
                                            <Card
                                                variant={grupoCajaSeleccionadoId === g.id ? 'elevation' : 'outlined'}
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => setGrupoCajaSeleccionadoId(g.id)}
                                            >
                                                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <input
                                                        type="radio"
                                                        checked={grupoCajaSeleccionadoId === g.id}
                                                        onChange={() => setGrupoCajaSeleccionadoId(g.id)}
                                                        style={{ marginRight: 8 }}
                                                    />
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 600 }}>{g.nombre}</Typography>
                                                        <Typography variant="caption">Código: {g.codigo}</Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                    {gruposCaja.length === 0 && !grupoCajaLoading && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2">No hay grupos de caja disponibles.</Typography>
                                        </Grid>
                                    )}
                                    {grupoCajaLoading && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2">Cargando más grupos...</Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
                        <Button
                            onClick={() => setOpenGrupoCajaDialog(false)}
                            fullWidth={isSmallScreen}
                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={() => void handleConfirmarGrupoCaja()}
                            variant="contained"
                            disabled={grupoCajaSeleccionadoId == null}
                            fullWidth={isSmallScreen}
                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                            Seleccionar
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Diálogo de selección de Banco */}
                <Dialog
                    open={openBancoDialog}
                    onClose={() => setOpenBancoDialog(false)}
                    maxWidth="sm"
                    fullWidth
                    fullScreen={isSmallScreen}
                    PaperProps={{
                        sx: {
                            m: { xs: 0, sm: 2 },
                            maxHeight: { xs: '100vh', sm: '90vh' }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' }, pb: { xs: 1, sm: 2 } }}>Seleccionar Banco</DialogTitle>
                    <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
                        <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                            <TextField
                                fullWidth
                                placeholder="Buscar banco..."
                                value={bancoSearch}
                                onChange={(e) => setBancoSearch(e.target.value)}
                                size={isSmallScreen ? "small" : "medium"}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon fontSize={isSmallScreen ? "small" : "medium"} />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Box>
                        <Box sx={{ maxHeight: { xs: '60vh', sm: 360 }, overflowY: 'auto', pr: 1 }} onScroll={handleBancoScroll}>
                            <Grid container spacing={1}>
                                {bancos.map((banco, idx) => (
                                    <Grid item xs={12} key={banco.id ?? idx}>
                                        <Card
                                            variant={bancoSeleccionadoId === banco.id ? 'elevation' : 'outlined'}
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => setBancoSeleccionadoId(banco.id)}
                                        >
                                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <input
                                                    type="radio"
                                                    checked={bancoSeleccionadoId === banco.id}
                                                    onChange={() => setBancoSeleccionadoId(banco.id)}
                                                    style={{ marginRight: 8 }}
                                                />
                                                <Typography sx={{ fontWeight: 600 }}>
                                                    {`${banco.nombre}${banco.nroBanco ? ` (${banco.nroBanco})` : ''}`}
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                                {!bancos.length && !bancoLoading && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2">No se encontraron bancos.</Typography>
                                    </Grid>
                                )}
                                {bancoLoading && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2">Cargando bancos...</Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
                        <Button 
                            onClick={() => setOpenBancoDialog(false)}
                            fullWidth={isSmallScreen}
                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            disabled={bancoSeleccionadoId == null}
                            fullWidth={isSmallScreen}
                            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                            onClick={() => {
                                const banco = bancos.find(b => b.id === bancoSeleccionadoId);
                                if (banco) {
                                    const label = `${banco.nombre}${banco.nroBanco ? ` (${banco.nroBanco})` : ''}`;
                                    const nroBancoNum = Number(banco.nroBanco);
                                    setPagoActual(prev => ({ ...prev, banco: label, nroBanco: Number.isFinite(nroBancoNum) ? nroBancoNum : undefined }));
                                }
                                setOpenBancoDialog(false);
                            }}
                        >
                            Seleccionar
                        </Button>
                    </DialogActions>
                </Dialog>
            </form>

            {/* Modal de búsqueda de Proveedors */}
            <ProveedorSearch
                open={openProveedorSearch}
                onClose={() => setOpenProveedorSearch(false)}
                onSelect={handleProveedorSelect}
            />

            {aplicarNcComp && formulario.nroProveedor > 0 && (
                <AplicarSaldoReciboDialog
                    open={aplicarNcDialogOpen}
                    onClose={handleCloseAplicarNcDialog}
                    reciboId={aplicarNcComp.nroFormulario!}
                    nroCliente={formulario.nroProveedor}
                    nombreCliente={formulario.nombre || `Proveedor ${formulario.nroProveedor}`}
                    numeroComprobanteCredito={
                        formatNumeroComprobante(aplicarNcComp.nroComprobante, aplicarNcComp.tipoComprobante)
                    }
                    variant="proveedor"
                    nroTipoFormularioCredito={aplicarNcComp.nroTipoFormulario ?? '3'}
                    importeTotalCredito={Math.abs(aplicarNcComp.importe || aplicarNcComp.saldo || 0)}
                    onSuccess={() => {
                        handleCloseAplicarNcDialog();
                        setRefreshComprobantesKey((k) => k + 1);
                    }}
                />
            )}

                    {/* Botón Cancelar al final */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: { xs: 3, sm: 4 } }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/tesoreria/orden-pago')}
                            fullWidth={isSmallScreen}
                            sx={{
                                borderRadius: 2,
                                px: { xs: 3, sm: 4 },
                                py: { xs: 1.5, sm: 1.5 },
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                            }}
                        >
                            Cancelar
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default NuevaOrdenPago; 
